import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation, confirmAction } from '../../lib/confirm';
import { Table } from '../../ui';
import {
  Card, Descriptions, Tag, Button, Space, Typography, Row, Col, Statistic, Tabs,
  Switch, Input, EmptyState, Image, Modal, Select, Spinner, Result, Popconfirm, Tooltip, theme, message, Upload,
  Alert,
} from '../../ui';
import {
  Pencil, PlayCircle, CheckCircle2, Ban, Users, Lock,
  Image as ImageIcon, Trash2, UserPlus, Flag, Upload as UploadIcon, Ticket, Phone, MapPin, Send,
  CalendarDays, Map as MapIcon, FileText, Check, Sparkles, Clock, Star, MessageSquare, ChevronDown, Search,
  AlertTriangle, Store, Sprout, Repeat, Hash, UserRound,
} from 'lucide-react';
import { api, request } from '../../api/client';
import { usePermission, useHasRole } from '../../store/auth';
import { useUrlTab } from '../../hooks/useUrlTab';
import { useRetailerSearch, useFarmerSearch } from '../../hooks/useRecordSearch';
import { brand } from '../../assets/brand';
import { formatIst } from '../../lib/time';
import { cn } from '../../ui/cn';
import { RetailerLink, UserLink } from '../../components/EntityLinks';
import { RoleBadge } from '../../components/RoleBadge';
import { CopyValue } from '../../components/CopyValue';
import { TerritoryChips } from '../../components/TerritoryChips';
import { EntityAvatar, entityHue } from '../../ui/EntityAvatar';
import { MiniMarkdown } from '../../components/MiniMarkdown';
import { EventFormModal } from './EventFormModal';
import { TYPE_LABEL, typeMeta, STATUS_TONE as STATUS_COLOR, statusLabel } from './eventMeta';

/** The shape every header fact shares - one pill, one glyph, one hue. */
const FACT = 'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 type-label [&>svg]:size-3.5 [&>svg]:shrink-0';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const NEXT: Record<string, string[]> = { PLANNED: ['ONGOING', 'COMPLETED', 'CANCELLED'], ONGOING: ['COMPLETED', 'CANCELLED'] };
const ACTION = (s: string) => (s === 'ONGOING' ? 'Start meet' : s === 'COMPLETED' ? 'Mark completed' : 'Cancel');

/**
 * The phases a meet passes through. Only three are STORED statuses; "Scheduled" and "Feedback" are
 * derived — a meet that is ready and waiting for its date, and one that is over but still collecting
 * verdicts. Deriving them means no extra status to keep in step across clients, and the phase falls
 * straight out of facts the payload already carries.
 *
 * CANCELLED is an exit, not a step, so it has no place on the track.
 */
const LIFECYCLE = [
  { key: 'PLANNED', label: 'Planned' },
  { key: 'SCHEDULED', label: 'Scheduled' },
  { key: 'ONGOING', label: 'Running' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'FEEDBACK', label: 'Feedback' },
] as const;

const PART_FILTER_LABEL: Record<string, string> = {
  invited: 'only those who were sent an invitation',
  pending: 'only those not yet invited',
  attended: 'only those who attended',
  absent: 'only those who did not attend',
  confirmed: 'only those who took a seat',
};
const STAR = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);
const TAG_LABEL: Record<string, string> = {
  CONTENT: 'Content', SPEAKER: 'Speaker', VENUE: 'Venue',
  TIMING: 'Timing', REFRESHMENTS: 'Refreshments', ORGANISATION: 'Organisation',
};

const TONE: Record<string, string> = {
  brand: 'bg-brand-500/12 text-brand-700 dark:text-brand-300',
  success: 'bg-success-500/12 text-success-600 dark:text-success-400',
  warn: 'bg-warning-500/15 text-warning-600 dark:text-warning-400',
  danger: 'bg-danger-500/12 text-danger-600 dark:text-danger-400',
};
const BAR: Record<string, string> = {
  brand: 'bg-brand-500', success: 'bg-success-500', warn: 'bg-warning-500', danger: 'bg-danger-500',
};

/**
 * One figure, its label, and the single line of context that makes it actionable.
 *
 * `of` draws a share bar rather than printing a second number beside the first: a denominator is the
 * same fact as its numerator, but two bare numbers under one label are two facts and read as a
 * contradiction.
 */
function StatTile({ label, value, icon, of, tone = 'brand', hint, onClick, active }: {
  label: string; value: number; icon: ReactNode; of?: number;
  tone?: 'brand' | 'success' | 'warn' | 'danger'; hint?: string;
  /** Filters the participant list to exactly the set this tile counted. */
  onClick?: () => void; active?: boolean;
}) {
  const pct = of && of > 0 ? Math.min(100, Math.round((value / of) * 100)) : null;
  const Root: any = onClick ? 'button' : 'div';
  return (
    <Root
      {...(onClick ? { type: 'button', onClick, 'aria-pressed': !!active } : {})}
      className={cn(
        // Elevation matches ui/StatCard: resting `shadow-e1`, selected (and hovered) one step up.
        // Emitted from mutually exclusive branches because cn() has no tailwind-merge — two
        // box-shadow utilities on one element resolve by stylesheet order, not class order.
        'w-full rounded-card border p-4 text-left transition-colors',
        active ? 'border-brand-500 bg-brand-500/[0.07] shadow-e2 ring-1 ring-brand-500/25' : 'border-line bg-surface shadow-e1',
        onClick && !active && 'hover:border-brand-300 hover:bg-surface-2/60 hover:shadow-e2',
      )}
    >
      <div className="flex items-center gap-2">
        <span className={cn('grid size-7 shrink-0 place-items-center rounded-full', TONE[tone])}>{icon}</span>
        <span className="type-overline text-subtle">{label}</span>
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className="type-metric text-content">{value}</span>
        {of != null && of > 0 && <span className="text-sm text-subtle">of {of}</span>}
      </div>
      {pct != null && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-surface-2">
          <div className={cn('h-full rounded-full transition-all', BAR[tone])} style={{ width: `${pct}%` }} />
        </div>
      )}
      {hint && <div className="mt-2 truncate text-xs text-subtle" title={hint}>{hint}</div>}
    </Root>
  );
}

/**
 * Accent per KIND of information, so the page can be navigated by colour rather than read in full:
 * timing is sky, the meet itself brand-green, the agenda violet, money gold, the outcome green.
 * The accent lives on a hairline and a tinted icon only — a fully-tinted card would compete with the
 * data inside it.
 */
const CARD_TONE: Record<string, { icon: string; rule: string; wash: string }> = {
  brand: { icon: 'bg-brand-500/12 text-brand-700 dark:text-brand-300', rule: 'border-t-brand-500', wash: 'from-brand-500/10' },
  sky: { icon: 'bg-sky-500/12 text-sky-600 dark:text-sky-400', rule: 'border-t-sky-500', wash: 'from-sky-500/10' },
  success: { icon: 'bg-success-500/12 text-success-600 dark:text-success-400', rule: 'border-t-success-500', wash: 'from-success-500/10' },
  warning: { icon: 'bg-warning-500/15 text-warning-600 dark:text-warning-400', rule: 'border-t-warning-500', wash: 'from-warning-500/12' },
  danger: { icon: 'bg-danger-500/12 text-danger-600 dark:text-danger-400', rule: 'border-t-danger-500', wash: 'from-danger-500/10' },
  harvest: { icon: 'bg-harvest-400/20 text-harvest-700 dark:text-harvest-300', rule: 'border-t-harvest-400', wash: 'from-harvest-400/16' },
  violet: { icon: 'bg-violet-500/12 text-violet-600 dark:text-violet-400', rule: 'border-t-violet-500', wash: 'from-violet-500/10' },
  neutral: { icon: 'bg-surface-2 text-muted', rule: 'border-t-line-strong', wash: 'from-surface-2' },
};

/**
 * The heading sits on a WASH of the card's own tone, with the rule under it — so a card reads as a
 * titled panel rather than a coloured hairline floating over a white sheet, which is what a column of
 * these looked like: six identical white rectangles distinguished only by 2px at the very top, the
 * one part of a card the eye never lands on.
 *
 * The wash fades out within the band, never behind the content: a fully tinted card competes with
 * the data inside it, and six of them turn a page into six coloured boxes.
 */
function ECard({ title, icon, tone = 'neutral', right, children }: {
  title: string; icon: ReactNode; tone?: keyof typeof CARD_TONE; right?: ReactNode; children: ReactNode;
}) {
  const t = CARD_TONE[tone] ?? CARD_TONE.neutral;
  return (
    <section className={cn('overflow-hidden rounded-card border border-t-2 border-line bg-surface shadow-e1', t.rule)}>
      <h4 className={cn('flex items-center gap-2 border-b border-line/70 bg-linear-to-b to-transparent px-4 py-2.5', t.wash)}>
        <span className={cn('grid size-6 shrink-0 place-items-center rounded-full', t.icon)}>{icon}</span>
        <span className="type-overline text-muted">{title}</span>
        {right && <span className="ml-auto min-w-0">{right}</span>}
      </h4>
      <div className="p-4">{children}</div>
    </section>
  );
}

/**
 * A labelled read-only field, in the app's three visible levels: the SECTION heading above is
 * uppercase, the label is sentence case and quiet, and the VALUE outranks its own label.
 *
 * The label used to be `type-overline` — the token that MEANS "section heading" — so "VENUE" and
 * "AGENDA / ABOUT" were drawn identically and a reader could not tell a field from the group it sat
 * in. Uppercase belongs to sections only.
 *
 * `prose` drops the value back to normal weight: an outcome write-up is a paragraph, and a bold
 * paragraph is not emphasis, it is just heavy. `empty` says what is missing in words — a lone
 * em-dash leaves the reader to guess whether it was never set or failed to load.
 */
function EField({ label, children, className, prose, empty, icon }: {
  label: string; children?: ReactNode; className?: string; prose?: boolean; empty?: string;
  /** A small glyph on the LABEL, marking what kind of fact this is — a clock for a time, a pin for a
   *  place. It varies per field or it is furniture: one icon repeated down a grid marks nothing. */
  icon?: ReactNode;
}) {
  return (
    <div className={className}>
      <dt className="flex items-center gap-1.5 type-caption text-subtle">
        {icon && <span className="grid shrink-0 place-items-center opacity-70 [&>svg]:size-3">{icon}</span>}
        {label}
      </dt>
      <dd className={cn('mt-0.5 text-content', prose ? 'type-body leading-relaxed' : 'type-body-strong')}>
        {children ?? <span className="font-normal text-subtle">{empty ?? 'Not recorded'}</span>}
      </dd>
    </div>
  );
}

export function EventDetailPage() {
  const { token } = theme.useToken();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = usePermission('EVENT.EDIT');
  const canInvite = usePermission('EVENT.INVITE');
  const canSeeFeedback = usePermission('EVENT.FEEDBACK.VIEW');
  const canGenerateReport = usePermission('EVENT.REPORT.GENERATE');
  // Rating is about who you ARE (an attendee), so role, not the auto-passing permission.
  const canRate = usePermission('EVENT.FEEDBACK.SUBMIT') && useHasRole('RETAILER', 'FARMER');
  /**
   * A seat belongs to a retailer or farmer — staff have no party of their own, and the server refuses
   * their registration outright. `usePermission` auto-passes for SUPER_ADMIN, so gating on the
   * permission alone put a Register button (and a my-registration fetch that 403'd on every page
   * load) in front of exactly the people who can never use it. `useHasRole` deliberately does not
   * auto-pass, which is what this question needs: who you ARE, not what you may do.
   */
  const canRegister = usePermission('EVENT.REGISTER') && useHasRole('RETAILER', 'FARMER');
  /**
   * Correcting a FROZEN record is a Super Admin act, and `usePermission` auto-passes for them — which
   * would be the wrong question anyway. This is who you ARE, so it is a role, like `canRegister`
   * above. It exists as a local fallback because `planCorrectable` is a field the SERVER computes:
   * against an API that predates it the action would simply vanish, and "the button is missing" is
   * indistinguishable from "you are not allowed" for the person looking at the screen.
   */
  const isSuperAdmin = useHasRole('SUPER_ADMIN');
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [inviteChoiceOpen, setInviteChoiceOpen] = useState(false);
  // In the URL so Back from a participant/retailer page returns to the tab you were on.
  const [tab, setTab] = useUrlTab('tab', ['overview', 'participants', 'feedback', 'report', 'outcome'] as const, 'overview');
  const [selectedKeys, setSelectedKeys] = useState<any[]>([]);
  /**
   * Which slice of the participant list is on screen. Driven by the tiles above it: a figure you
   * cannot open is a figure you have to go and re-derive by hand, and on a 200-person list "who
   * still has not been invited" is the whole question.
   */
  const [partFilter, setPartFilter] = useState<'all' | 'invited' | 'pending' | 'attended' | 'absent' | 'confirmed'>('all');

  const { data: ev, isLoading, isError } = useQuery({ queryKey: ['event', id], queryFn: () => request<any>({ url: `/events/${id}` }), enabled: !!id });
  // Read before the early returns below, so the feedback queries can be declared unconditionally.
  const ev0Status: string | undefined = (ev as any)?.status;
  const { data: participants } = useQuery({ queryKey: ['event-participants', id], queryFn: () => request<any[]>({ url: `/events/${id}/participants` }), enabled: !!id });
  const { data: photos } = useQuery({ queryKey: ['event-photos', id], queryFn: () => request<any[]>({ url: `/events/${id}/photos` }), enabled: !!id });

  // Feedback is only meaningful once the meet is over, so nothing is fetched before then.
  const isDone = ev0Status === 'COMPLETED';
  const { data: fbSummary } = useQuery({
    queryKey: ['event-feedback-summary', id],
    queryFn: () => request<any>({ url: `/events/${id}/feedback/summary` }),
    enabled: !!id && isDone && canSeeFeedback,
  });
  const { data: fbList } = useQuery({
    queryKey: ['event-feedback', id],
    queryFn: () => request<any[]>({ url: `/events/${id}/feedback` }),
    enabled: !!id && isDone && canSeeFeedback,
  });
  const { data: myFb } = useQuery({
    queryKey: ['event-feedback-mine', id],
    queryFn: () => request<any>({ url: `/events/${id}/feedback/mine` }),
    enabled: !!id && isDone && canRate,
  });

  // A customer's own seat. Only fetched for accounts that can hold one.
  const { data: myReg } = useQuery({
    queryKey: ['event-my-registration', id],
    queryFn: () => request<any>({ url: `/events/${id}/my-registration` }),
    enabled: !!id && canRegister,
  });

  const registerMe = useConfirmedMutation({ title: "Register yourself for this event?", okText: "Yes, register me" }, {
    mutationFn: () => request<any>({ url: `/events/${id}/register`, method: 'POST' }),
    onSuccess: (r: any) => {
      message.success(r?.alreadyRegistered ? 'You are already registered' : 'Seat confirmed — give your name at the door');
      qc.invalidateQueries({ queryKey: ['event-my-registration', id] });
      qc.invalidateQueries({ queryKey: ['event', id] });
    },
    // The server is the authority on "full": seats can go between render and click, so the error is
    // shown as-is rather than being second-guessed from a possibly stale count.
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not register for this event.'),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['event', id] });
    qc.invalidateQueries({ queryKey: ['event-participants', id] });
    qc.invalidateQueries({ queryKey: ['events'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };

  const change = useMutation({
    mutationFn: (status: string) => request({ url: `/events/${id}/status`, method: 'PATCH', data: { status } }),
    onSuccess: () => { message.success('Event updated'); invalidate(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const setAttendance = useConfirmedMutation((v) => ({ title: v.attended ? "Mark this participant as attended?" : "Mark this participant as not attended?", okText: "Yes" }), {
    mutationFn: ({ partId, attended }: { partId: string; attended: boolean }) =>
      request({ url: `/events/${id}/participants/${partId}/attendance`, method: 'PATCH', data: { attended } }),
    // The Attended tile is computed on the EVENT, not the participant list, so refreshing only the
    // list left the toggle on and the tile reading 0 — the same fact disagreeing with itself.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-participants', id] });
      qc.invalidateQueries({ queryKey: ['event', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const removePart = useMutation({
    mutationFn: (partId: string) => request({ url: `/events/${id}/participants/${partId}`, method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['event-participants', id] }); qc.invalidateQueries({ queryKey: ['event', id] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const autoFill = useMutation({
    mutationFn: () => request<any[]>({ url: `/events/${id}/participants/auto-fill`, method: 'POST' }),
    onSuccess: (list: any) => {
      const n = Array.isArray(list) ? list.length : 0;
      const added = n - assigned;
      // "0 participants on the list" was reported as a success. Say what the click actually did.
      if (added > 0) message.success(`${added} added — ${n} on the list now. Send invites to notify them.`);
      else message.info('Everyone in this territory is already on the list.');
      qc.invalidateQueries({ queryKey: ['event-participants', id] });
      qc.invalidateQueries({ queryKey: ['event', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const sendInvites = useConfirmedMutation((scope) => ({ title: scope === "ALL" ? "Send invites to everyone on the list?" : "Send invites to everyone not yet notified?", content: scope === "ALL" ? "People who were already invited get a second message." : "Only participants who have not been messaged yet are contacted.", okText: "Yes, send" }), {
    mutationFn: (scope: 'PENDING' | 'ALL') =>
      request<any>({ url: `/events/${id}/invites`, method: 'POST', data: { scope } }),
    onSuccess: (r: any) => {
      // Report what actually happened, including who could not be reached: "sent 40, 20 have no app
      // account" is the number an organiser has to act on BEFORE the meet, not discover after it.
      const parts = [`Invited ${r?.sent ?? 0}`];
      if (r?.unreachable) parts.push(`${r.unreachable} could not be reached (no app account)`);
      if (r?.alreadyInvited) parts.push(`${r.alreadyInvited} already invited`);
      message.success(parts.join(' · '));
      setInviteChoiceOpen(false);
      invalidate();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not send invitations'),
  });

  /**
   * With nobody invited yet there is no decision to make, so the button just sends. Once some have
   * been told and others have not, sending is ambiguous — "the 3 new ones" and "all 8" are both
   * reasonable readings — and guessing either way is how people get messaged twice or not at all.
   */
  const onSendInvites = () => (notified > 0 ? setInviteChoiceOpen(true) : sendInvites.mutate('PENDING'));

  const rateIt = useConfirmedMutation({ title: "Submit your feedback?", content: "You can’t change it once it is in.", okText: "Yes, submit" }, {
    mutationFn: (body: { rating: number; tags?: string[]; remark?: string }) =>
      request<any>({ url: `/events/${id}/feedback`, method: 'POST', data: body }),
    onSuccess: (r: any) => {
      message.success(r?.revised ? 'Your rating was updated' : 'Thanks — your rating was recorded');
      qc.invalidateQueries({ queryKey: ['event-feedback-mine', id] });
      qc.invalidateQueries({ queryKey: ['event', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not save your rating'),
  });
  const remindFeedback = useConfirmedMutation({ title: "Remind everyone who has not given feedback?", content: "A reminder goes to each participant still outstanding.", okText: "Yes, remind them" }, {
    mutationFn: () => request<any>({ url: `/events/${id}/feedback/remind`, method: 'POST' }),
    onSuccess: (r: any) => {
      message.success(`Reminder sent to ${r?.asked ?? 0} attendee${r?.asked === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: ['event-feedback-summary', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not send the reminder'),
  });
  const makeReport = useConfirmedMutation({ title: "Generate the event report?", content: "It is rebuilt from the attendance and feedback recorded so far.", okText: "Yes, generate" }, {
    mutationFn: () => request<any>({ url: `/events/${id}/report`, method: 'POST' }),
    onSuccess: (r: any) => {
      message.success(r?.reportSource === 'AI' ? 'Report generated' : 'Report generated from the figures (AI model unreachable)');
      qc.invalidateQueries({ queryKey: ['event', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not generate the report'),
  });

  const afterBulk = () => { setSelectedKeys([]); qc.invalidateQueries({ queryKey: ['event-participants', id] }); qc.invalidateQueries({ queryKey: ['event', id] }); };
  const bulkAttend = useConfirmedMutation((attended) => ({ title: attended ? "Mark the selected participants as attended?" : "Mark the selected participants as not attended?", okText: "Yes" }), {
    mutationFn: (attended: boolean) => request({ url: `/events/${id}/participants/bulk-attendance`, method: 'POST', data: { ids: selectedKeys, attended } }),
    onSuccess: (_d, attended) => { message.success(attended ? `Marked ${selectedKeys.length} attended` : `Marked ${selectedKeys.length} not attended`); afterBulk(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const bulkRemove = useMutation({
    mutationFn: () => request({ url: `/events/${id}/participants/bulk-remove`, method: 'POST', data: { ids: selectedKeys } }),
    onSuccess: () => { message.success(`Removed ${selectedKeys.length} participant(s)`); afterBulk(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const addPhotos = useConfirmedMutation({ title: "Add these photos to the event?", okText: "Yes, add" }, {
    mutationFn: (urls: string[]) => request({ url: `/events/${id}/photos`, method: 'POST', data: { urls } }),
    onSuccess: (_d, urls) => { message.success(`${urls.length} photo${urls.length === 1 ? '' : 's'} added`); qc.invalidateQueries({ queryKey: ['event-photos', id] }); qc.invalidateQueries({ queryKey: ['event', id] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const removePhoto = useConfirmedMutation({ title: "Remove this photo?", content: "It is taken off the event record and can’t be recovered.", okText: "Yes, remove", danger: true }, {
    mutationFn: (photoId: string) => request({ url: `/events/${id}/photos/${photoId}`, method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['event-photos', id] }); qc.invalidateQueries({ queryKey: ['event', id] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  if (isLoading) return <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>;
  if (isError || !ev) return <Result status="error" title="Couldn't load this event" extra={<Button variant="primary" onClick={() => navigate('/events')}>Back to events</Button>} />;

  /**
   * Only the next legitimate step is offered. From PLANNED that is "Start meet" — a meet cannot have
   * been completed before it began, and showing both as equally-weighted green buttons made closing
   * an un-started meet look like a normal choice. A meet that really did happen but was never
   * started is closed with two clicks (start, then complete), which is also the truthful record.
   * The server still tolerates PLANNED -> COMPLETED so no other client breaks on this.
   */
  const transitions = (NEXT[ev.status] ?? []).filter((s) => !(s === 'COMPLETED' && ev.status === 'PLANNED'));
  // Mirrors the server's EVENT_NOT_STARTED_YET guard: a meet cannot have been held before it begins.
  const notStartedYet = !!ev.startAt && new Date(ev.startAt).getTime() > Date.now();
  // On the list vs actually notified. `invitedCount` is the legacy alias of the list size.
  const assigned = ev.assignedCount ?? ev.invitedCount ?? 0;
  const notified = ev.notifiedCount ?? 0;
  const pendingInvites = ev.pendingInviteCount ?? Math.max(0, assigned - notified);
  const invitesOpen = ev.status === 'PLANNED' || ev.status === 'ONGOING';
  const feedbackOpen = !!ev.feedbackOpen;
  const attendanceOpen = ev.attendanceOpen ?? (ev.status === 'ONGOING' || ev.status === 'COMPLETED');
  /**
   * What still stops this meet being started. Mirrors the server's EVENT_NOT_READY guard so the
   * button is never offered for a click that must fail, and names everything at once rather than
   * surfacing one problem per attempt.
   */
  // Compared by DAY, matching the server: starting at 09:45 for a 10:00 meet is normal.
  const istDayOf = (d: any) => new Date(new Date(d).getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
  const startsLater = !!ev.startAt && istDayOf(ev.startAt) > istDayOf(new Date());
  /**
   * Each blocker carries THE ACT THAT CLEARS IT, because they are cleared in different places and
   * only one of them is on this page. The tooltip used to offer "Edit or the Participants tab" for
   * whichever problem you had — so a meet with no territory sent you to a tab with nothing on it
   * that can set one (the territory is a field of the plan, and only the Edit dialog writes it).
   * Waiting for the date is not a blocker anyone can fix, so it names no act at all.
   */
  const startBlockers = ([
    !ev.territoryId ? { text: 'no territory is set', fix: 'Set the territory with Edit' } : null,
    assigned === 0 ? { text: 'nobody is on the participant list', fix: 'Add people on the Participants tab' } : null,
    startsLater ? { text: `it is scheduled for ${formatIst(ev.startAt)}`, fix: null } : null,
  ].filter(Boolean) as { text: string; fix: string | null }[]);
  const startBlockerWords = startBlockers.map((b) => b.text).join(' and ');
  /** The fixes, as a sentence — "Set the territory with Edit. Add people on the Participants tab." */
  const startBlockerFixes = startBlockers.map((b) => b.fix).filter(Boolean).join('. ');
  /**
   * THE TURNOUT FUNNEL, and the two things it must not claim.
   *
   * 1. Seats are only a live fact while seats can still be taken. A completed meet was showing
   *    "Seats taken 0 / 40" beside "Attended 19" — 40 is the EXPECTED attendance, `confirmedCount`
   *    counts only customers who confirmed a seat themselves, and at a territory meet where everyone
   *    is auto-invited nobody ever does. So the row read as a failure when nothing had failed. It is
   *    a question for a meet that has not happened yet, and it is asked only then.
   * 2. Every gap is named. A bar that stops short says something is missing but not what, and the
   *    reader's next question ("who?") is the one the tab behind it answers.
   */
  const seatsLive = (ev.registrationOpen ?? (ev.status === 'PLANNED' || ev.status === 'ONGOING')) && ev.capacity > 0;
  const attendedCount = Number(ev.attendedCount ?? 0);
  const turnoutRows: { label: string; value: number; of: number; bar: string; note?: string | null }[] = [
    {
      label: 'Invited', value: notified, of: assigned, bar: 'bg-sky-500',
      note: assigned === 0 ? 'nobody on the list yet'
        : pendingInvites > 0 ? `${pendingInvites} never told` : 'everyone was told',
    },
    {
      label: 'Attended', value: attendedCount, of: assigned, bar: 'bg-success-500',
      note: ev.status === 'PLANNED' ? 'the meet has not started'
        : ev.status === 'CANCELLED' ? 'the meet was called off'
          : assigned === 0 ? null
            : attendedCount >= assigned ? 'everyone on the list came'
              : `${assigned - attendedCount} did not come`,
    },
    ...(seatsLive ? [{
      label: 'Seats confirmed', value: Number(ev.confirmedCount ?? 0), of: Number(ev.capacity), bar: 'bg-harvest-400',
      note: Number(ev.confirmedCount ?? 0) === 0
        ? 'nobody has claimed a seat yet'
        : `${ev.seatsLeft} of ${ev.capacity} still free`,
    }] : []),
  ];

  /**
   * Editing the PLAN and correcting a closed RECORD are different acts, so they are named
   * differently and gated separately. The server owns both rules (PLAN_EDITABLE / PLAN_CORRECTABLE
   * in event.service.ts) and refuses the write either way; each `??` spells the same rule out so the
   * action still appears against an API released before the flags existed.
   */
  const planEditable: boolean = ev.planEditable ?? (ev.status !== 'CANCELLED' && ev.status !== 'COMPLETED');
  const canCorrectPlan: boolean = ev.planCorrectable ?? (!planEditable && isSuperAdmin);

  const attendanceClosedReason = ev.status === 'PLANNED'
    ? 'This meet has not started yet — attendance opens when you start it.'
    : 'This meet was cancelled, so it has no attendance.';
  // The one thing the organiser should do next, in the lifecycle strip.
  /**
   * Which phase the track should highlight. PLANNED splits in two on readiness: still being set up
   * versus ready and waiting for the day. COMPLETED splits on whether the feedback window is open.
   */
  const phase = ev.status === 'PLANNED'
    ? (startBlockers.length ? 'PLANNED' : 'SCHEDULED')
    : ev.status === 'COMPLETED'
      ? (feedbackOpen ? 'FEEDBACK' : 'COMPLETED')
      : ev.status;
  const phaseIndex = LIFECYCLE.findIndex((x) => x.key === phase);
  /**
   * The moment each phase began.
   *
   * PLANNED is when the record was created; RUNNING is `startedAt`, which is the meet's real start
   * and not the time it was scheduled for; COMPLETED and the feedback window come from their own
   * stamps. SCHEDULED is deliberately blank — it is a state the event drifts into once it is ready,
   * not something that happens at an instant, and inventing a timestamp for it would be a lie.
   */
  const shortIst = (d?: string | null) => (d ? formatIst(d) : null);
  const phaseAt: Record<string, string | null> = {
    PLANNED: shortIst(ev.createdAt),
    SCHEDULED: null,
    ONGOING: shortIst(ev.startedAt),
    COMPLETED: shortIst(ev.completedAt),
    FEEDBACK: ev.feedbackClosesAt ? `closes ${formatIst(ev.feedbackClosesAt)}` : null,
  };
  /* Feedback keyed by party, so a participant row can show what that person said. Deliberately NOT a
     useMemo: this sits after the early returns above, and a hook that only runs once the event has
     loaded changes the hook count between renders — React refuses the second render outright. */
  const openParticipants = (f: typeof partFilter) => {
    setPartFilter((cur) => (cur === f && tab === 'participants' ? 'all' : f));
    setTab('participants');
  };
  const shownParticipants = (participants ?? []).filter((p: any) => (
    partFilter === 'invited' ? p.invited
      : partFilter === 'pending' ? !p.invited
        : partFilter === 'attended' ? p.attended
          : partFilter === 'absent' ? !p.attended
            : partFilter === 'confirmed' ? !!p.confirmedAt
              : true
  ));
  const ratingByParty = new Map<string, any>((fbList ?? []).map((f: any) => [`${f.partyType}:${f.partyId}`, f]));

  /**
   * THE NEXT MOVE, IN THE COLOUR OF WHAT KIND OF MOVE IT IS.
   *
   * This was one grey sentence at the end of the track, in the same grey as the phase captions under
   * it - the one line on the card that says what to DO, dressed as furniture. It is a capsule now,
   * and the hue says which kind of line it is before it is read: amber when the meet is BLOCKED,
   * sky or harvest when there is work in hand, violet while a window is open and nothing is owed but
   * waiting, green when the thing is done. The glyph names the act, so it varies with the step.
   */
  const nextStep: { text: string; icon: ReactNode; chip: string } | null = ev.status === 'PLANNED'
    ? (startBlockers.length
      ? { text: `Cannot start yet — ${startBlockerWords}`, icon: <AlertTriangle />, chip: 'border-warning-500/35 bg-warning-500/12 text-warning-600 dark:text-harvest-300' }
      : assigned === 0
        ? { text: 'Add participants, then send invites', icon: <UserPlus />, chip: 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300' }
        : pendingInvites > 0
          ? { text: `${pendingInvites} invitation${pendingInvites === 1 ? '' : 's'} still to send`, icon: <Send />, chip: 'border-harvest-500/35 bg-harvest-400/15 text-harvest-700 dark:text-harvest-300' }
          : { text: 'Everyone invited — start the meet on the day', icon: <PlayCircle />, chip: 'border-brand-500/30 bg-brand-500/10 text-brand-700 dark:text-brand-300' })
    : ev.status === 'ONGOING'
      ? { text: 'Mark who attended, then complete the meet', icon: <Flag />, chip: 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300' }
      : ev.status === 'COMPLETED'
        ? (feedbackOpen
          ? { text: `Collecting feedback — closes ${ev.feedbackClosesAt ? formatIst(ev.feedbackClosesAt) : 'soon'}`, icon: <MessageSquare />, chip: 'border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300' }
          : ev.hasReport
            ? { text: 'Report ready', icon: <CheckCircle2 />, chip: 'border-brand-500/30 bg-brand-500/10 text-brand-700 dark:text-brand-300' }
            : { text: 'Feedback closed — generate the report', icon: <FileText />, chip: 'border-harvest-500/35 bg-harvest-400/15 text-harvest-700 dark:text-harvest-300' })
        : null;

  /**
   * Adding a NAMED person and INVITING A TERRITORY are different acts, and a finished meet answers
   * them differently - so they get separate gates rather than one "is it terminal" test.
   *
   * After the meet, adding someone is recording a walk-in who turned up without being on the list;
   * that has to stay possible, or the attendance register can only ever be corrected downwards and
   * the organiser's workaround is to leave the event un-completed. Bulk-inviting a territory to a
   * meet that already happened invents thousands of invitees who were never asked, so it closes with
   * the event. A cancelled meet had no attendees at all, so nothing can be recorded against it.
   */
  const isCompleted = ev.status === 'COMPLETED';
  const canAddPeople = ev.status !== 'CANCELLED';
  const addLabel = isCompleted ? 'Add attendee' : 'Add participants';
  const addHint = isCompleted
    ? 'Record someone who came but was never on the list - they are marked as attended.'
    : 'Add specific retailers & farmers to this meet';
  const addClosedReason = 'This event was cancelled, so nobody can be added to it.';
  // The territory-invite reason keeps the customer-facing Alert's phrasing.
  const inviteClosedReason = `This event is ${statusLabel(ev.status).toLowerCase()}, so the territory can no longer be invited.`;
  /** The meet's own colour and glyph — the same pair the list, the filter and the form draw. */
  const evType = typeMeta(ev.type);

  return (
    <div>
      <Card className="mb-3">
        <Row align="middle" gutter={16}>
          <Col flex="auto">
            {/* The code is an eyebrow, not a peer of the title — it identifies the record, the title
                names the meet. The rest were a dot-separated run in which every fact had the same
                weight and none could be found; they are labelled chips now. */}
            <div className="type-overline text-subtle">{ev.code}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <h1 className="type-page-title text-content">{ev.title}</h1>
              <Tag tone={STATUS_COLOR[ev.status] as any}>{statusLabel(ev.status)}</Tag>
              {ev.cadence && ev.cadence !== 'ONE_TIME' && <Tag>{String(ev.cadence).toLowerCase()}</Tag>}
            </div>
            {/*
              * WHAT / WHEN / WHERE / WHOSE PATCH / WHO RAN IT - each fact in the colour of what it IS.
              *
              * These were five grey runs behind five grey glyphs of equal weight: a reader looking for
              * "where" had to read all five to find it. Each is a capsule now, and the hue carries the
              * kind of fact - the meet's own type colour, calendar blue for the date, harvest for the
              * venue, and the app-wide territory palette for the patch, which also spells the chain
              * (state, district, beat) the raw name could not. The organiser wears the identity mark
              * and the role pill they wear on every other screen, so "who ran it" is recognised rather
              * than read.
              */}
            <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <span className={cn(FACT, evType.chip)} title="What kind of meet this is">
                <evType.Icon />{evType.label}
              </span>
              <span className={cn(FACT, 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300')} title="When it starts">
                <CalendarDays />{formatIst(ev.startAt)}
              </span>
              {ev.venue && (
                <span className={cn(FACT, 'border-harvest-500/35 bg-harvest-400/15 text-harvest-700 dark:text-harvest-300')} title="Venue">
                  <MapPin />{ev.venue}
                </span>
              )}
              {(ev.territoryId || ev.territoryName) && (
                <TerritoryChips territoryId={ev.territoryId} fallbackName={ev.territoryName} wrap />
              )}
              {ev.organiserName && (
                <span className="inline-flex items-center gap-1.5 rounded-pill border border-line bg-surface py-0.5 pl-0.5 pr-2 type-label text-content shadow-e1" title="Organiser">
                  <EntityAvatar name={ev.organiserName} size={20} />
                  <UserLink id={ev.organiserUserId} role={ev.organiserRole}>{ev.organiserName}</UserLink>
                  <RoleBadge role={ev.organiserRole} />
                </span>
              )}
            </div>
          </Col>
          {canEdit && (
            <Col flex="none">
              <Space wrap>
                {/* Editing the PLAN and correcting a closed RECORD are different acts, so they are
                    named differently. Both open the same dialog, which offers whichever the server
                    says this reader may do — see planEditable / planCorrectable in event.service.ts. */}
                {planEditable ? (
                  <Button variant="default" icon={<Pencil size={16} />} onClick={() => setEditOpen(true)}>Edit</Button>
                ) : canCorrectPlan ? (
                  <Tooltip label="This meet is closed, so its plan is part of the record. The title and the venue can still be corrected.">
                    <Button variant="default" icon={<Lock size={16} />} onClick={() => setEditOpen(true)}>Correct record</Button>
                  </Tooltip>
                ) : null}
                {transitions.map((s) => {
                  if (s === 'CANCELLED') {
                    return (
                      <Popconfirm key={s} title="Cancel this event?" okText="Cancel event" okButtonProps={{ danger: true }} onConfirm={() => change.mutate(s)}>
                        <Button variant="danger" icon={<Ban size={16} />} loading={change.isPending && change.variables === s}>Cancel</Button>
                      </Popconfirm>
                    );
                  }
                  /**
                   * Only ONE forward action is the obvious next step, and on a PLANNED event that is
                   * "Start meet" — offering two equally-weighted green buttons made closing a meet
                   * that had not begun look like a normal choice. Completing straight from PLANNED
                   * stays available (organisers often never open the app on the day) but as a quiet
                   * secondary, and not at all before the meet's own start time has arrived.
                   */
                  const isComplete = s === 'COMPLETED';
                  if (s === 'ONGOING' && startBlockers.length) {
                    return (
                      <Tooltip key={s} label={`Cannot start this meet yet — ${startBlockerWords}.${startBlockerFixes ? ` ${startBlockerFixes}.` : ''}`}>
                        <span>
                          <Button variant="primary" icon={<PlayCircle size={16} />} disabled>Start meet</Button>
                        </span>
                      </Tooltip>
                    );
                  }
                  if (isComplete && notStartedYet) {
                    return (
                      <Tooltip key={s} label={`This meet starts ${formatIst(ev.startAt)}. It cannot be completed before then.`}>
                        <span>
                          <Button variant="default" icon={<CheckCircle2 size={16} />} disabled>Mark completed</Button>
                        </span>
                      </Tooltip>
                    );
                  }
                  return (
                    <Button
                      key={s}
                      variant={isComplete && ev.status === 'PLANNED' ? 'default' : 'primary'}
                      icon={s === 'ONGOING' ? <PlayCircle size={16} /> : <CheckCircle2 size={16} />}
                      // Say what actually happens (certification DEF-043): starting a meet notifies nobody; completing it asks
                      // the people marked as attended for feedback. "Everyone on the event is told" was true of neither.
                      onClick={() => confirmAction({
                        title: `Move this event to ${s.replace(/_/g, ' ').toLowerCase()}?`,
                        content: s === 'ONGOING'
                          ? 'The meet is marked as started. Participants are not sent a notification about it.'
                          : 'The meet is marked as completed, and everyone marked as attended is asked for feedback.',
                        okText: 'Yes, change it',
                      }, () => change.mutate(s))} loading={change.isPending && change.variables === s}
                    >
                      {ACTION(s)}
                    </Button>
                  );
                })}
              </Space>
            </Col>
          )}
        </Row>
      </Card>

      {/* A customer's own seat. Register is hidden outright once the event is terminal, and disabled
          (rather than left clickable to fail) once the last seat has gone — the audit found a submit
          that was accepted and then rejected with a generic error. */}
      {canRegister && (
        <Card style={{ marginBottom: 12 }}>
          {myReg?.confirmedAt ? (
            <Space align="start" wrap size={16}>
              <div>
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                  {myReg.checkedInAt ? 'You attended this meet' : 'Your seat is confirmed'}
                </Typography.Title>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 4 }}>
                  {myReg.checkedInAt
                    ? `Recorded as attended on ${formatIst(myReg.checkedInAt)}.`
                    : 'Give your name at the door — the organiser has you on the list.'}
                </Typography.Paragraph>
                <Tag tone={myReg.checkedInAt ? 'green' : 'blue'}>{statusLabel(myReg.status)}</Tag>
              </div>
            </Space>
          ) : !ev.registrationOpen ? (
            <Alert
              type="info"
              title="Registration is closed"
              description={`This event is ${statusLabel(ev.status).toLowerCase()}, so it is no longer taking registrations.`}
            />
          ) : ev.seatsLeft === 0 ? (
            <Alert
              type="warning"
              title="Event full"
              description={`All ${ev.capacity} seats have been taken. Contact your sales representative if you need a place.`}
            />
          ) : (
            <Space align="center" wrap>
              <Button
                variant="primary"
                icon={<Ticket size={16} />}
                loading={registerMe.isPending}
                onClick={() => registerMe.mutate()}
              >
                Register for this event
              </Button>
              {ev.seatsLeft != null && (
                <Typography.Text type="secondary">
                  {ev.seatsLeft} of {ev.capacity} seat{ev.capacity === 1 ? '' : 's'} left
                </Typography.Text>
              )}
            </Space>
          )}
        </Card>
      )}

      {/* On the list and Invited are separate facts, so they are separate tiles. Merging them is how
          this screen came to report thousands of invitations that were never sent. */}
      {/* The attendee's own verdict. Sits where their seat card sits, because it is the same slot in
          the same story: before the meet it tells them they have a place, after it asks how it went. */}
      {canRate && isDone && myFb?.canRate && (
        <Card className="mb-3">
          <RateMeetCard
            mine={myFb.mine}
            windowOpen={!!myFb.windowOpen}
            closesAt={myFb.closesAt}
            saving={rateIt.isPending}
            onSubmit={(body) => rateIt.mutate(body)}
          />
        </Card>
      )}

      {/* Where the meet is in its life, and what still has to happen. Reading a status word out of a
          tag told you the state but not the next move. */}
      {ev.status !== 'CANCELLED' && (
        <Card className="mb-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
            {LIFECYCLE.map((st, i) => {
              const done = phaseIndex > i;
              const here = phase === st.key;
              return (
                <div key={st.key} className="flex items-center gap-2">
                  <span className={cn(
                    'grid size-7 shrink-0 place-items-center rounded-full border type-label-strong',
                    done && 'border-brand-500 bg-brand-500 text-white',
                    here && 'border-brand-500 text-brand-700 ring-4 ring-brand-500/15 dark:text-brand-300',
                    !done && !here && 'border-line text-subtle',
                  )}>
                    {done ? <Check size={14} /> : i + 1}
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className={cn('text-sm', here ? 'font-semibold text-content' : done ? 'text-muted' : 'text-subtle')}>
                      {st.label}
                    </span>
                    {/* When it happened, not just that it did. A phase with no recorded moment says
                        nothing rather than guessing one. */}
                    {phaseAt[st.key] && (
                      <span className="whitespace-nowrap type-caption text-subtle">{phaseAt[st.key]}</span>
                    )}
                  </span>
                  {i < LIFECYCLE.length - 1 && (
                    <span className={cn('mx-1 hidden h-px w-8 sm:block lg:w-14', done ? 'bg-brand-500' : 'bg-line')} />
                  )}
                </div>
              );
            })}
            {nextStep && (
              <span className={cn('ml-auto inline-flex items-center gap-2 rounded-pill border px-3 py-1.5 type-body-medium [&>svg]:size-4 [&>svg]:shrink-0', nextStep.chip)}>
                {nextStep.icon}{nextStep.text}
              </span>
            )}
          </div>
        </Card>
      )}

      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="On the list" value={assigned} icon={<Users size={15} />}
          hint="Added from the territory or by hand"
          active={tab === 'participants' && partFilter === 'all'}
          onClick={() => openParticipants('all')}
        />
        <StatTile
          label="Invited" value={notified} icon={<Send size={15} />} of={assigned}
          active={tab === 'participants' && partFilter === 'invited'}
          onClick={() => openParticipants('invited')}
          tone={pendingInvites > 0 && invitesOpen ? 'warn' : 'brand'}
          /* "13 still to notify" is a call to action, and there is no action once the meet is over —
             nobody needs inviting to something that already happened. Past the event the same number
             is a fact about how it was run, so it is stated as one. */
          hint={assigned === 0 ? 'Nobody on the list'
            : invitesOpen
              ? (pendingInvites > 0 ? `${pendingInvites} still to notify` : 'Everyone has been told')
              : (pendingInvites > 0 ? `${pendingInvites} were never invited` : 'Everyone was invited')}
        />
        <StatTile
          label="Attended" value={ev.attendedCount ?? 0} icon={<Flag size={15} />} of={assigned} tone="success"
          hint={ev.status === 'COMPLETED' ? 'Final' : 'Marked so far'}
          active={tab === 'participants' && partFilter === 'attended'}
          onClick={() => openParticipants('attended')}
        />
        {/* The fourth tile answers whatever the event's stage makes most useful: seats while people
            are still taking them, the verdict once it is over, the gallery otherwise. */}
        {isDone && (fbSummary?.responses ?? 0) > 0 ? (
          <StatTile
            label="Average rating" value={fbSummary.average} icon={<Star size={15} />} of={5}
            tone={fbSummary.average >= 4 ? 'success' : fbSummary.average >= 3 ? 'warn' : 'danger'}
            hint={`${fbSummary.responses} of ${fbSummary.attended} attendees replied`}
            active={tab === 'feedback'}
            onClick={() => setTab('feedback')}
          />
        ) : ev.capacity > 0 && !isDone ? (
          <StatTile
            label="Seats taken" value={ev.confirmedCount ?? 0} of={ev.capacity}
            icon={<Ticket size={15} />} tone={ev.seatsLeft === 0 ? 'danger' : 'brand'}
            hint={ev.seatsLeft === 0 ? 'Full' : `${ev.seatsLeft} left of ${ev.capacity}`}
            active={tab === 'participants' && partFilter === 'confirmed'}
            onClick={() => openParticipants('confirmed')}
          />
        ) : (
          <StatTile
            label="Photos" value={ev.photoCount ?? 0} icon={<ImageIcon size={15} />}
            hint={ev.photoCount ? 'Added to the outcome report' : 'None added yet'}
            active={tab === 'outcome'}
            onClick={() => setTab('outcome')}
          />
        )}
      </div>

      <Card>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'overview', label: 'Overview',
              children: (
                /* Cards, not a form.
                   The previous layout was one long two-column list of label/value pairs: every
                   section carried the same weight, empty fields left holes across the middle, and
                   with nothing but grey labels on white there was no way to find anything by eye.
                   Each concern is now a card with its own accent, so colour means WHAT KIND of fact
                   it is — timing, plan, money, turnout, verdict — and an empty field costs a line
                   rather than a hole. */
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  <div className="space-y-4 lg:col-span-2">
                    {/* ONE card, not two thin ones.
                        Two half-width cards of four fields each ran a column of air down the middle
                        of both, and split one subject — what this meet IS — across two headings. The
                        same eight facts in a three-up grid fill the width, and each wears the mark
                        its own kind of fact already has elsewhere on the page: the type its chip, the
                        area its territory chips, the organiser their avatar and role badge, the
                        reference a click to copy. */}
                    <ECard title="The meet" icon={<CalendarDays size={15} />} tone="sky">
                      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                        <EField label="Starts" icon={<Clock />}>{formatIst(ev.startAt)}</EField>
                        <EField label="Ends" icon={<Clock />} empty="No finish time set">{ev.endAt ? formatIst(ev.endAt) : undefined}</EField>
                        <EField label="Type" icon={<evType.Icon />}>
                          <span className={cn(FACT, evType.chip)}><evType.Icon />{evType.label}</span>
                        </EField>
                        <EField label="Venue" icon={<MapPin />} empty="No venue recorded" className="col-span-2">{ev.venue ?? undefined}</EField>
                        <EField label="Cadence" icon={<Repeat />}>{(ev.cadence ?? 'ONE_TIME').toLowerCase().replace(/_/g, ' ')}</EField>
                        <EField label="Area" icon={<MapIcon />} empty="No area on file" className="col-span-2">
                          {(ev.territoryId || ev.territoryName)
                            ? <TerritoryChips territoryId={ev.territoryId} fallbackName={ev.territoryName} wrap />
                            : undefined}
                        </EField>
                        <EField label="Reference" icon={<Hash />}>
                          <CopyValue value={ev.code} label="Event no">
                            <span className="font-mono text-xs">{ev.code}</span>
                          </CopyValue>
                        </EField>
                        <EField label="Organiser" icon={<UserRound />} empty="Nobody recorded" className="col-span-2">
                          {ev.organiserName ? (
                            <span className="inline-flex min-w-0 items-center gap-1.5">
                              <EntityAvatar name={ev.organiserName} size={20} />
                              <UserLink id={ev.organiserUserId} role={ev.organiserRole}>{ev.organiserName}</UserLink>
                              <RoleBadge role={ev.organiserRole} />
                            </span>
                          ) : undefined}
                        </EField>
                      </dl>
                    </ECard>

                    {/* The agenda is what everyone was told they were coming to, and what the outcome
                        below is measured against — so it reads as a quoted passage rather than as one
                        more grey paragraph in a strip. */}
                    <ECard title="Agenda / about" icon={<FileText size={15} />} tone="violet">
                      <p className="whitespace-pre-wrap border-l-2 border-violet-500/40 pl-3.5 text-[15px] leading-relaxed text-content">
                        {ev.description || <span className="text-sm text-subtle">No agenda was written for this meet.</span>}
                      </p>
                    </ECard>

                    {isCompleted && (
                      <ECard title="What came of it" icon={<CheckCircle2 size={15} />} tone="success">
                        {(ev.outcomeSummary || ev.decisions || ev.discussionNotes) ? (
                          /* Three different answers — what happened, what was agreed, what was
                             discussed — so three bounded blocks. Stacked as bare label/value pairs
                             they ran together into one column of prose in which no answer could be
                             found without reading the one above it. */
                          <dl className="grid grid-cols-1 gap-3">
                            {ev.outcomeSummary && (
                              <div className="rounded-card border border-success-500/25 bg-success-500/[0.06] p-3">
                                <EField label="Outcome" icon={<Flag />} prose><span className="whitespace-pre-wrap">{ev.outcomeSummary}</span></EField>
                              </div>
                            )}
                            {ev.decisions && (
                              <div className="rounded-card border border-brand-500/25 bg-brand-500/[0.06] p-3">
                                <EField label="Decisions" icon={<Check />} prose><span className="whitespace-pre-wrap">{ev.decisions}</span></EField>
                              </div>
                            )}
                            {ev.discussionNotes && (
                              <div className="rounded-card border border-line bg-surface-2/60 p-3">
                                <EField label="Discussion" icon={<MessageSquare />} prose><span className="whitespace-pre-wrap">{ev.discussionNotes}</span></EField>
                              </div>
                            )}
                          </dl>
                        ) : (
                          <p className="text-sm text-subtle">Nothing recorded yet — write it on the Outcome &amp; Photos tab.</p>
                        )}
                      </ECard>
                    )}

                    {(photos?.length ?? 0) > 0 && (
                      <ECard title={`Photos (${ev.photoCount ?? photos?.length})`} icon={<ImageIcon size={15} />} tone="harvest">
                        <Gallery photos={(photos ?? []).slice(0, 8)} />
                        {(photos?.length ?? 0) > 8 && (
                          <Button variant="default" size="sm" onClick={() => setTab('outcome')} style={{ marginTop: 10 }}>
                            View all {photos?.length} photos
                          </Button>
                        )}
                      </ECard>
                    )}
                  </div>

                  <div className="space-y-4">
                    {/* The verdict leads the rail once there is one — it is the first thing anyone
                        opening a finished meet wants, and it carries the score's own colour. */}
                    {isDone && canSeeFeedback && (
                      <ECard
                        title="Verdict"
                        icon={<Star size={15} />}
                        tone={(fbSummary?.average ?? 0) >= 4 ? 'success' : (fbSummary?.average ?? 0) >= 3 ? 'warning' : fbSummary?.responses ? 'danger' : 'neutral'}
                      >
                        {fbSummary?.responses ? (
                          <>
                            <div className="flex items-baseline gap-2">
                              <span className={cn('type-metric',
                                fbSummary.average >= 4 ? 'text-success-600' : fbSummary.average >= 3 ? 'text-warning-600' : 'text-danger-600')}>
                                {fbSummary.average}
                              </span>
                              <span className="text-sm text-subtle">of 5</span>
                            </div>
                            <div className="mt-1 text-xl leading-none text-harvest-500">{STAR(Math.round(fbSummary.average))}</div>
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2">
                              <div className="h-full rounded-full bg-brand-500" style={{ width: `${fbSummary.responseRate ?? 0}%` }} />
                            </div>
                            <div className="mt-1.5 text-xs text-subtle">
                              {fbSummary.responses} of {fbSummary.attended} attendees replied ({fbSummary.responseRate}%)
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-subtle">No ratings yet.</p>
                        )}
                        <Button variant="default" size="sm" onClick={() => setTab(ev.hasReport ? 'report' : 'feedback')} style={{ marginTop: 12 }}>
                          {ev.hasReport ? 'Open the report' : 'See feedback'}
                        </Button>
                      </ECard>
                    )}

                    <ECard title="Turnout" icon={<Flag size={15} />} tone="brand">
                      {/* THE BASE, then the fractions of it.
                          "On the list" used to be drawn as a bar against itself — a meter that is
                          100% full on every event that has ever existed, which tells a reader
                          nothing. It is the denominator the other two are measured against, so it is
                          stated as one. */}
                      <div className="mb-3 flex items-baseline justify-between gap-3 border-b border-line pb-2.5">
                        <span className="type-caption text-subtle">On the list</span>
                        <span className="type-body-strong tabular-nums text-content">{assigned}</span>
                      </div>
                      {/* Bars, not a column of right-aligned digits: the comparison between them is
                          the point, and numbers in a list do not make one. */}
                      {turnoutRows.map((r) => (
                        <div key={r.label} className="mb-2.5 last:mb-0">
                          <div className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="text-muted">{r.label}</span>
                            <span className="tabular-nums text-content">
                              {r.value}<span className="text-subtle"> / {r.of}</span>
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-2">
                            <div className={cn('h-full rounded-full', r.bar)} style={{ width: `${r.of > 0 ? Math.min(100, (r.value / r.of) * 100) : 0}%` }} />
                          </div>
                          {r.note && <div className="mt-1 type-caption text-subtle">{r.note}</div>}
                        </div>
                      ))}
                      {/* Once the meet is over the comparison worth printing is plan against reality,
                          and it is a sentence rather than a fourth bar — the expected figure is the
                          Planning card's, not another share of the list. */}
                      {isDone && ev.capacity > 0 && (
                        <p className="mt-3 border-t border-line pt-2.5 type-caption text-muted">
                          {(ev.attendedCount ?? 0) > 0
                            ? `${Math.round(((ev.attendedCount ?? 0) / ev.capacity) * 100)}% of the attendance planned for.`
                            : 'No attendance was ever marked, so there is nothing to compare with the plan.'}
                        </p>
                      )}
                    </ECard>

                    <ECard title="Planning" icon={<Ticket size={15} />} tone="harvest">
                      <dl className="space-y-3">
                        <EField label="Expected attendees">{ev.capacity ? String(ev.capacity) : undefined}</EField>
                        <EField label="Budget">{ev.budget != null ? inr(ev.budget) : undefined}</EField>
                        <EField label="Cost per attendee">
                          {ev.budget > 0 && (ev.attendedCount ?? 0) > 0
                            ? `${inr(Number(ev.budget) / ev.attendedCount)} · ${ev.attendedCount} attended`
                            : undefined}
                        </EField>
                      </dl>
                    </ECard>

                    <ECard title="Timeline" icon={<Clock size={15} />} tone="neutral">
                      <ol>
                        {[
                          ['Planned', ev.createdAt, ev.organiserName ? `by ${ev.organiserName}` : null, 'bg-sky-500'],
                          ['Invitations sent', ev.invitesSentAt, `${notified} of ${assigned} notified`, 'bg-sky-500'],
                          ['Started', ev.startedAt, null, 'bg-brand-500'],
                          ['Completed', ev.completedAt, null, 'bg-success-500'],
                          ['Cancelled', ev.cancelledAt, null, 'bg-danger-500'],
                          ['Feedback closes', ev.feedbackClosesAt, `${fbSummary?.responses ?? 0} of ${fbSummary?.attended ?? ev.attendedCount ?? 0} replied`, 'bg-harvest-400'],
                          ['Report generated', ev.reportGeneratedAt, ev.reportSource === 'AI' ? 'AI summary' : ev.reportSource ? 'figures only' : null, 'bg-violet-500'],
                        ].filter(([, at]) => !!at).map(([label, at, note, dot], i, arr) => (
                          <li key={String(label)} className="flex gap-3">
                            <div className="flex flex-col items-center">
                              <span className={cn('mt-1.5 size-2.5 shrink-0 rounded-full ring-2 ring-surface', dot as string)} />
                              {i < arr.length - 1 && <span className="w-px flex-1 bg-line" />}
                            </div>
                            <div className="pb-3.5">
                              <div className="text-sm text-content">{label as string}</div>
                              <div className="text-xs text-subtle">{formatIst(at as string)}{note ? ` · ${note}` : ''}</div>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </ECard>
                  </div>
                </div>
              ),
            },
            {
              key: 'participants', label: `Participants (${ev.invitedCount ?? 0})`,
              children: (
                <>
                  {(canEdit || (canInvite && invitesOpen)) && (
                    <Space style={{ marginBottom: 12 }} wrap>
                      {/* Being on the list is not being invited. This is the act that tells people. */}
                      {canInvite && invitesOpen && (
                        <Tooltip label={pendingInvites
                          ? `Notify the ${pendingInvites} participant${pendingInvites === 1 ? '' : 's'} who have not been invited yet`
                          : assigned ? 'Everyone on the list has already been invited' : 'Add participants first'}>
                          <span>
                            <Button
                              variant="primary"
                              icon={<Send size={16} />}
                              disabled={!pendingInvites}
                              loading={sendInvites.isPending}
                              onClick={onSendInvites}
                            >
                              {pendingInvites ? `Send invites (${pendingInvites})` : 'Invites sent'}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {canEdit && <>
                      {/* After the meet this button changes MEANING - it records a walk-in rather than
                          inviting anyone - so it changes its label with it. Disabled only on a
                          cancelled event, and disabled rather than hidden because a vanished button
                          reads as a missing permission. */}
                      <Tooltip label={canAddPeople ? addHint : addClosedReason}>
                        <span>
                          <Button
                            variant={isCompleted ? 'default' : 'primary'}
                            icon={<UserPlus size={16} />}
                            disabled={!canAddPeople}
                            onClick={() => setAddOpen(true)}
                          >
                            {addLabel}
                          </Button>
                        </span>
                      </Tooltip>
                      {/* The disabled case drops the Popconfirm entirely rather than wrapping a dead
                          trigger: a disabled child emits no pointer events, so the confirm would never
                          open and the click would be silently swallowed. */}
                      {ev.registrationOpen && !ev.territoryId ? (
                        <Tooltip label="This event has no territory, so there is nobody to add. Set one with Edit first.">
                          <span>
                            <Button variant="default" icon={<Users size={16} />} disabled>Add whole territory</Button>
                          </span>
                        </Tooltip>
                      ) : ev.registrationOpen ? (
                        <Popconfirm
                          title="Add the whole territory to the list?"
                          description="Adds every retailer & farmer of this event's territory (and its sub-areas) to the list. They are NOT notified until you send invites."
                          okText="Add all" onConfirm={() => autoFill.mutate()}
                        >
                          <Tooltip label="New events auto-fill the territory; use this to re-sync or fill an older event. Adding is not inviting.">
                            <Button variant="default" icon={<Users size={16} />} loading={autoFill.isPending}>Add whole territory</Button>
                          </Tooltip>
                        </Popconfirm>
                      ) : (
                        <Tooltip label={inviteClosedReason}>
                          <span>
                            <Button variant="default" icon={<Users size={16} />} disabled>Add whole territory</Button>
                          </span>
                        </Tooltip>
                      )}
                      {selectedKeys.length > 0 && (
                        <>
                          <span style={{ color: token.colorTextTertiary }}>·</span>
                          {attendanceOpen && <>
                            <Button variant="default" icon={<Flag size={16} />} loading={bulkAttend.isPending} onClick={() => bulkAttend.mutate(true)}>Mark attended ({selectedKeys.length})</Button>
                            <Button variant="default" loading={bulkAttend.isPending} onClick={() => bulkAttend.mutate(false)}>Mark not attended</Button>
                          </>}
                          <Popconfirm title={`Remove ${selectedKeys.length} participant(s)?`} okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => bulkRemove.mutate()}>
                            <Button variant="danger" icon={<Trash2 size={16} />} loading={bulkRemove.isPending}>Remove ({selectedKeys.length})</Button>
                          </Popconfirm>
                        </>
                      )}
                      </>}
                    </Space>
                  )}
                  {partFilter !== 'all' && (
                    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-brand-500/30 bg-brand-500/[0.06] px-3 py-2">
                      <span className="text-sm text-content">
                        Showing <strong>{shownParticipants.length}</strong> of {participants?.length ?? 0} — {PART_FILTER_LABEL[partFilter]}
                      </span>
                      <Button variant="default" size="sm" onClick={() => setPartFilter('all')} className="ml-auto">Show everyone</Button>
                    </div>
                  )}
                  <Table
                    rowKey="id" size="small" dataSource={shownParticipants}
                    /* A territory auto-fill puts the whole area on this list — 900 rows rendered as
                       43,000 DOM nodes and took 4.5s to paint, and a real district meet is larger
                       still. Paged in the client: the rows are already fetched, it is the DOM that
                       does not scale. */
                    pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: [25, 50, 100], hideOnSinglePage: true,
                      showTotal: (total: number, range: [number, number]) => `${range[0]}–${range[1]} of ${total}` }}
                    rowSelection={canEdit ? { selectedRowKeys: selectedKeys, onChange: (keys) => setSelectedKeys(keys) } : undefined}
                    locale={{ emptyText: <EmptyState title={
                      ev.registrationOpen ? 'No invitees yet — add the retailers & farmers for this meet.'
                        : isCompleted ? 'Nobody was recorded at this meet — add anyone who attended.'
                          : 'This meet was cancelled, so nobody was recorded.'} /> }}
                    columns={[
                      /* The row used to carry the name and a dot-joined sub-line, leaving most of the
                         width empty. The facts an organiser actually works from — where someone is,
                         how to reach them, when they were told, what they thought — each get a
                         column, so the row can be scanned down instead of read across. */
                      {
                        title: 'Name',
                        render: (_: any, p: any) => {
                          /* A retailer has a detail page, so the name opens it (RetailerLink is
                             permission-gated and never links for customer roles). Farmers have no
                             such route — they open in a modal from the People register — so the id
                             is shown rather than a link that would land nowhere useful. */
                          const label = <span className="truncate font-medium">{p.name}</span>;
                          return (
                            <div className="flex min-w-0 items-center gap-2">
                              {p.partyType === 'RETAILER'
                                ? <RetailerLink id={p.retailerId} className="truncate font-medium">{p.name}</RetailerLink>
                                : label}
                              {/* The business code, not the row id: R-100015 means something to the
                                  person reading it, #389 is an implementation detail. */}
                              {p.code && (
                                <span className="shrink-0 rounded border border-line bg-surface-2 px-1 py-px font-mono type-caption text-subtle">{p.code}</span>
                              )}
                              <Tag tone={p.partyType === 'RETAILER' ? 'blue' : 'green'}>{p.partyType}</Tag>
                            </div>
                          );
                        },
                      },
                      {
                        title: 'Territory', width: 210,
                        // The beat is what a rep actually works in, so it is named, with how specific it is.
                        render: (_: any, p: any) => (p.territoryName ? (
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <span className="flex min-w-0 items-center gap-1.5 text-sm">
                              <MapIcon size={13} className="shrink-0 text-subtle" />
                              <span className="truncate">{p.territoryName}</span>
                            </span>
                            <span className="flex items-center gap-1.5 type-caption text-subtle">
                              {p.territoryCode && <span className="font-mono">{p.territoryCode}</span>}
                              {p.territoryLevel && <span className="capitalize">{p.territoryLevel}</span>}
                            </span>
                          </div>
                        ) : <span className="text-subtle">—</span>),
                      },
                      {
                        title: 'Place', dataIndex: 'place', width: 150,
                        render: (v: string) => (v
                          ? <span className="inline-flex items-center gap-1.5 text-sm"><MapPin size={13} className="shrink-0 text-subtle" />{v}</span>
                          : <span className="text-subtle">—</span>),
                      },
                      {
                        title: 'Contact', dataIndex: 'mobile', width: 170,
                        // A tel: link, because the reason to look at this column is to ring somebody.
                        render: (v: string) => (v
                          ? <a href={`tel:${v}`} className="inline-flex items-center gap-1.5 text-sm tabular-nums text-brand-700 hover:underline dark:text-brand-400">
                              <Phone size={13} className="shrink-0" />{v}
                            </a>
                          : <span className="text-subtle">—</span>),
                      },
                      {
                        title: 'Invited', dataIndex: 'invited', width: 190,
                        render: (v: boolean, p: any) => (v
                          ? <span className="whitespace-nowrap text-sm text-muted">{formatIst(p.invitedAt)}</span>
                          : <Tag>Not sent</Tag>),
                      },
                      {
                        title: 'Attended', dataIndex: 'attended', width: 150,
                        render: (v: boolean, p: any) => {
                          // A live toggle on a meet that has not started invites an attendance figure
                          // for something that has not happened, and everything downstream — the
                          // outcome report, the post-event feedback request — is built on it.
                          if (!canEdit || !attendanceOpen) {
                            return v ? <Tag tone="green">Yes</Tag>
                              : <Tooltip label={attendanceOpen ? 'Not marked' : attendanceClosedReason}><span className="text-subtle">—</span></Tooltip>;
                          }
                          return (
                            <div className="flex flex-col gap-0.5">
                              <Switch checked={v} onCheckedChange={(c) => setAttendance.mutate({ partId: p.id, attended: c })} />
                              {v && p.attendedAt && <span className="whitespace-nowrap type-caption text-subtle">{formatIst(p.attendedAt)}</span>}
                            </div>
                          );
                        },
                      },
                      /* Once the meet is over the row's last useful fact is what that person thought
                         of it — which is also how you find the 1-star to ring back. */
                      ...(isDone && canSeeFeedback ? [{
                        title: 'Rating', width: 230,
                        render: (_: any, p: any) => {
                          const fb = ratingByParty.get(`${p.partyType}:${p.retailerId ?? p.farmerId}`);
                          if (!fb) return <span className="text-subtle">—</span>;
                          return (
                            <div className="flex flex-col gap-0.5">
                              <span className={cn('text-sm', fb.rating >= 4 ? 'text-success-600' : fb.rating === 3 ? 'text-warning-600' : 'text-danger-600')}>
                                {STAR(fb.rating)}
                              </span>
                              {fb.remark && (
                                // A tooltip can neither scroll nor wrap off-screen, so it only carries
                                // text short enough to read in one. Anything longer is clamped here and
                                // read in full on the Feedback tab, which is built for it.
                                fb.remark.length <= 280 ? (
                                  <Tooltip label={fb.remark}>
                                    <span className="line-clamp-2 type-caption text-subtle">{fb.remark}</span>
                                  </Tooltip>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setTab('feedback')}
                                    className="line-clamp-2 text-left type-caption text-subtle hover:text-brand-700 dark:hover:text-brand-400"
                                    title="Read the full comment on the Feedback tab"
                                  >
                                    {fb.remark}
                                  </button>
                                )
                              )}
                            </div>
                          );
                        },
                      } as any] : []),
                      ...(canEdit ? [{
                        title: '', width: 50,
                        render: (_: any, p: any) => (
                          <Popconfirm title="Remove this participant?" onConfirm={() => removePart.mutate(p.id)}>
                            <Button size="sm" variant="soft-danger" icon={<Trash2 size={16} />} aria-label="Remove participant" />
                          </Popconfirm>
                        ),
                      } as any] : []),
                    ]}
                  />
                </>
              ),
            },
            ...(isDone && canSeeFeedback ? [{
              key: 'feedback',
              label: `Feedback (${fbSummary?.responses ?? 0})`,
              children: (
                <FeedbackPanel
                  summary={fbSummary}
                  responses={fbList ?? []}
                  event={ev}
                  canRemind={canInvite}
                  canGenerate={canGenerateReport}
                  reminding={remindFeedback.isPending}
                  generating={makeReport.isPending}
                  onRemind={() => remindFeedback.mutate()}
                  onGenerate={() => makeReport.mutate()}
                  onShowPending={() => openParticipants('attended')}
                />
              ),
            }, {
              key: 'report',
              label: 'Report',
              children: (
                <ReportPanel
                  event={ev}
                  summary={fbSummary}
                  responses={fbList ?? []}
                  canGenerate={canGenerateReport}
                  generating={makeReport.isPending}
                  onGenerate={() => makeReport.mutate()}
                />
              ),
            }] : []),
            {
              key: 'outcome', label: 'Outcome & Photos',
              children: <OutcomeTab ev={ev} photos={photos ?? []} canEdit={canEdit} onAddPhotos={(urls) => addPhotos.mutate(urls)} onRemovePhoto={(pid) => removePhoto.mutate(pid)} onSaved={invalidate} />,
            },
          ]}
        />
      </Card>

      <EventFormModal open={editOpen} onClose={() => setEditOpen(false)} record={ev} />
      {/* Two readings of one click, so the organiser picks rather than the code guessing. */}
      <Modal
        open={inviteChoiceOpen}
        onOpenChange={(o: boolean) => { if (!o) setInviteChoiceOpen(false); }}
        title="Who should get the invitation?"
        width={520}
        footer={<Button variant="default" onClick={() => setInviteChoiceOpen(false)}>Cancel</Button>}
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: -6 }}>
          {notified} of {assigned} on this list {notified === 1 ? 'has' : 'have'} already been invited.
        </Typography.Paragraph>
        <div className="space-y-2">
          <button
            type="button"
            disabled={!pendingInvites || sendInvites.isPending}
            onClick={() => sendInvites.mutate('PENDING')}
            className="w-full rounded-card border border-line p-3 text-left transition-colors hover:border-brand-400 hover:bg-brand-50 disabled:opacity-50 dark:hover:bg-brand-900/20"
          >
            <div className="type-body-strong text-content">
              Only the {pendingInvites} not yet invited
            </div>
            <div className="mt-0.5 text-xs text-subtle">
              Nobody hears about this meet twice. This is almost always what you want.
            </div>
          </button>
          <button
            type="button"
            disabled={sendInvites.isPending}
            onClick={() => sendInvites.mutate('ALL')}
            className="w-full rounded-card border border-line p-3 text-left transition-colors hover:border-brand-400 hover:bg-brand-50 disabled:opacity-50 dark:hover:bg-brand-900/20"
          >
            <div className="type-body-strong text-content">Everyone on the list ({assigned})</div>
            <div className="mt-0.5 text-xs text-subtle">
              Sends a second notification to the {notified} already invited — use it as a reminder.
            </div>
          </button>
        </div>
      </Modal>

      <AddParticipantsModal open={addOpen} onClose={() => setAddOpen(false)} eventId={id} territoryId={ev.territoryId} existing={participants ?? []} recordingAttendance={isCompleted} onDone={() => { setAddOpen(false); qc.invalidateQueries({ queryKey: ['event-participants', id] }); qc.invalidateQueries({ queryKey: ['event', id] }); }} />
    </div>
  );
}

// ── Outcome + photo gallery tab ───────────────────────────────────────────
function OutcomeTab({ ev, photos, canEdit, onAddPhotos, onRemovePhoto, onSaved }:
  { ev: any; photos: any[]; canEdit: boolean; onAddPhotos: (urls: string[]) => void; onRemovePhoto: (id: string) => void; onSaved: () => void }) {
  const [summary, setSummary] = useState(ev.outcomeSummary ?? '');
  const [decisions, setDecisions] = useState(ev.decisions ?? '');
  const [discussion, setDiscussion] = useState(ev.discussionNotes ?? '');
  useEffect(() => { setSummary(ev.outcomeSummary ?? ''); setDecisions(ev.decisions ?? ''); setDiscussion(ev.discussionNotes ?? ''); }, [ev.id, ev.outcomeSummary, ev.decisions, ev.discussionNotes]);

  const save = useConfirmedMutation({ title: "Save the event outcome?", okText: "Yes, save" }, {
    mutationFn: () => request({ url: `/events/${ev.id}`, method: 'PATCH', data: { outcomeSummary: summary, decisions, discussionNotes: discussion } }),
    onSuccess: () => { message.success('Outcome saved'); onSaved(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  if (!canEdit) {
    return (
      <div>
        <Field label="Summary" value={ev.outcomeSummary} />
        <Field label="Decisions taken" value={ev.decisions} />
        <Field label="Discussion points" value={ev.discussionNotes} />
        <Typography.Title level={5} style={{ marginTop: 16 }}>Photos</Typography.Title>
        <Gallery photos={photos} />
      </div>
    );
  }

  return (
    <div>
      <Typography.Paragraph type="secondary" style={{ marginTop: -6 }}>
        Post what happened at the meet — a short summary, the decisions taken, and the key discussion points. Add photos to the gallery below.
      </Typography.Paragraph>
      <div style={{ marginBottom: 4, fontWeight: 500 }}>Summary</div>
      <Input.TextArea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What happened at the meet…" style={{ marginBottom: 12 }} />
      <div style={{ marginBottom: 4, fontWeight: 500 }}>Decisions taken</div>
      <Input.TextArea rows={3} value={decisions} onChange={(e) => setDecisions(e.target.value)} placeholder="Decisions / action items…" style={{ marginBottom: 12 }} />
      <div style={{ marginBottom: 4, fontWeight: 500 }}>Discussion points</div>
      <Input.TextArea rows={3} value={discussion} onChange={(e) => setDiscussion(e.target.value)} placeholder="What was discussed…" style={{ marginBottom: 12 }} />
      <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save outcome</Button>

      <Typography.Title level={5} style={{ marginTop: 20 }}>Photos ({photos.length})</Typography.Title>
      <div style={{ marginBottom: 12 }}>
        <MultiPhotoUpload onAdd={onAddPhotos} />
        <Typography.Text type="secondary" style={{ marginLeft: 10, fontSize: 12 }}>Select multiple images at once · JPG/PNG · up to 5 MB each</Typography.Text>
      </div>
      <Gallery photos={photos} onRemove={onRemovePhoto} />
    </div>
  );
}

// Multi-select photo uploader: each chosen image is uploaded to /uploads/event-photo, and the
// resulting URLs are batched (debounced) into a single "add photos" call.
function MultiPhotoUpload({ onAdd }: { onAdd: (urls: string[]) => void }) {
  const pending = useRef<string[]>([]);
  const timer = useRef<any>(null);
  const [busy, setBusy] = useState(false);
  const flush = () => { const urls = pending.current; pending.current = []; setBusy(false); if (urls.length) onAdd(urls); };
  const beforeUpload = async (file: any) => {
    if (!String(file.type || '').startsWith('image/')) { message.error(`${file.name}: images only`); return Upload.LIST_IGNORE; }
    if (file.size > 5 * 1024 * 1024) { message.error(`${file.name}: max 5 MB`); return Upload.LIST_IGNORE; }
    setBusy(true);
    try {
      const form = new FormData(); form.append('file', file);
      const r = await api.post('/uploads/event-photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.data?.url;
      if (url) { pending.current.push(url); clearTimeout(timer.current); timer.current = setTimeout(flush, 600); }
    } catch { message.error(`${file.name}: upload failed`); }
    return Upload.LIST_IGNORE;
  };
  return (
    <Upload multiple showUploadList={false} accept="image/*" beforeUpload={beforeUpload}>
      <Button variant="default" icon={<UploadIcon size={16} />} loading={busy}>Add photos</Button>
    </Upload>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 500, marginBottom: 2 }}>{label}</div>
      <div style={{ whiteSpace: 'pre-wrap', color: value ? undefined : '#bbb' }}>{value || 'Not posted yet'}</div>
    </div>
  );
}

/**
 * The meet's photographs, as a grid that FILLS its card.
 *
 * They were 140px squares in a wrapping flex row: three of them left two thirds of the card empty,
 * portrait shots were cropped to squares, and the row re-flowed into a ragged shape at every width.
 * A grid gives every tile the same 4:3 frame and the same share of the row, so a wall of photos of a
 * meet reads as one thing rather than as loose stamps — and the delete control only appears on the
 * tile the pointer is on, instead of a red button parked over every picture.
 */
function Gallery({ photos, onRemove }: { photos: any[]; onRemove?: (id: string) => void }) {
  if (!photos.length) return <EmptyState title="No photos yet" />;
  return (
    <Image.PreviewGroup>
      {/* TILES ARE SIZED, NOT COUNTED.
          A fixed column count makes the thumbnail a function of whatever it is sitting in: three
          columns is a comfortable 300px inside the Overview card and a 600px poster on the
          full-width Outcome tab, where the same three photos filled the screen. `auto-fill` with a
          minimum instead: the tile stays around 10rem wherever it is, and the COLUMN COUNT is what
          varies with the space. A gallery is a contact sheet — you click one to see it big. */}
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(10rem,1fr))]">
        {photos.map((p) => (
          <div key={p.id} className="group relative overflow-hidden rounded-card border border-line bg-surface-2 shadow-e1 transition-shadow hover:shadow-e2">
            <Image
              src={p.url}
              className="aspect-[4/3] w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
            />
            {onRemove && (
              <Popconfirm title="Remove this photo?" onConfirm={() => onRemove(p.id)}>
                <Button
                  size="sm" variant="danger" icon={<Trash2 size={16} />} aria-label="Remove photo"
                  className="absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                />
              </Popconfirm>
            )}
          </div>
        ))}
      </div>
    </Image.PreviewGroup>
  );
}

// ── Add participants modal (retailers + farmers of the event's territory) ──
/** One person's verdict. Used by the feedback list and by the report's themed groups alike. */
function CommentCard({ r }: { r: any }) {
  return (
    <div
      className={cn(
        'rounded-card border border-l-4 border-line p-3',
        r.rating >= 4 ? 'border-l-success-500' : r.rating === 3 ? 'border-l-warning-500' : 'border-l-danger-500',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={cn('text-sm leading-none', r.rating >= 4 ? 'text-success-600' : r.rating === 3 ? 'text-warning-600' : 'text-danger-600')}>
          {STAR(r.rating)}
        </span>
        <span className="min-w-0 truncate type-body-medium text-content">{r.name}</span>
        {r.source === 'ON_BEHALF' && <Tag tone="gold">via staff</Tag>}
        {r.submittedAt && <span className="ml-auto shrink-0 type-caption text-subtle">{formatIst(r.submittedAt)}</span>}
      </div>
      {r.remark && <Remark text={r.remark} />}
      {!!r.tags?.length && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {r.tags.map((t: string) => <Tag key={t}>{TAG_LABEL[t] ?? t}</Tag>)}
        </div>
      )}
    </div>
  );
}

/**
 * Attendee comments, grouped by the theme they flagged.
 *
 * A flat grid works while there are a dozen; at several hundred it is a wall nobody reads, and
 * paginating it only makes the wall arrive in instalments. Grouped, five hundred comments become six
 * themes with a count and an average apiece — "Timing · 48 mentions · 2.4★" is a finding, where the
 * forty-eight quotations underneath it are the evidence for one.
 *
 * Critical ratings are lifted out first and never collapsed: they are the half that needs somebody to
 * act, and burying them inside a theme would be exactly the wrong economy.
 */
function ThemedComments({ items, tagCounts, criticalTotal, commentsTotal }: {
  /** The fetched page of responses — the quotations. */
  items: any[];
  /** TRUE totals, counted in SQL over every response. The headline numbers must come from these:
   *  the fetched page is capped, and a theme labelled "45 mentions" beside a chip reading "174" is
   *  the same fact contradicting itself on one screen. */
  tagCounts: Record<string, number>;
  criticalTotal: number;
  commentsTotal: number;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [criticalShown, setCriticalShown] = useState(20);
  const critical = items.filter((r) => r.rating <= 2);
  const rest = items.filter((r) => r.rating > 2);

  const groups = new Map<string, any[]>();
  for (const r of rest) {
    const keys: string[] = r.tags?.length ? r.tags : ['__none'];
    for (const k of keys) groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  const ordered = [...groups.entries()]
    .map(([k, list]) => ({
      key: k,
      label: k === '__none' ? 'No theme given' : (TAG_LABEL[k] ?? k),
      list,
      // How many flagged it in total; `list` is only the examples we hold.
      total: k === '__none' ? list.length : (tagCounts?.[k] ?? list.length),
      avg: list.reduce((a, r) => a + r.rating, 0) / list.length,
    }))
    .sort((a, b) => b.total - a.total);
  const biggest = ordered[0]?.total || 1;

  return (
    <div className="space-y-4">
      {!!critical.length && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="type-overline text-danger-600 dark:text-danger-500">Needs attention</span>
            <span className="text-xs text-subtle">
              {criticalTotal} rated 2★ or below
              {critical.length < criticalTotal ? ` · ${critical.length} shown` : ''}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {critical.slice(0, criticalShown).map((r) => <CommentCard key={r.id} r={r} />)}
          </div>
          {/* Critical ratings are the half worth reading in full, but "in full" still has to fit on a
              screen — a thousand-person meet can produce hundreds. */}
          {critical.length > criticalShown && (
            <div className="mt-3 text-center">
              <Button variant="default" size="sm" onClick={() => setCriticalShown((v) => v + 40)}>
                Show more ({critical.length - criticalShown} left)
              </Button>
            </div>
          )}
        </div>
      )}

      <div>
        <div className="mb-2 type-overline text-muted">By theme</div>
        <div className="space-y-2">
          {ordered.map((g) => {
            const isOpen = open === g.key;
            const tone = g.avg >= 4 ? 'text-success-600' : g.avg >= 3 ? 'text-warning-600' : 'text-danger-600';
            return (
              <div key={g.key} className="overflow-hidden rounded-card border border-line">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : g.key)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-surface-2/60"
                >
                  <span className="type-body-medium text-content">{g.label}</span>
                  <span className="text-xs text-subtle">{g.total} {g.total === 1 ? 'mention' : 'mentions'}</span>
                  <span className={cn('text-xs tabular-nums', tone)}>{g.avg.toFixed(1)}★ avg</span>
                  {/* The share bar makes the biggest themes findable without reading the counts. */}
                  <span className="ml-auto hidden h-1.5 w-24 overflow-hidden rounded-full bg-surface-2 sm:block">
                    <span
                      className={cn('block h-full rounded-full', g.avg >= 4 ? 'bg-success-500' : g.avg >= 3 ? 'bg-warning-500' : 'bg-danger-500')}
                      style={{ width: `${(g.total / biggest) * 100}%` }}
                    />
                  </span>
                  <ChevronDown size={15} className={cn('shrink-0 text-subtle transition-transform', isOpen && 'rotate-180')} />
                </button>
                {isOpen && (
                  <div className="border-t border-line bg-surface-2/30 p-3">
                    {g.total > g.list.length && (
                      <p className="mb-2 text-xs text-subtle">
                        Showing {g.list.length} of {g.total} — the lowest-rated first. Use the Feedback tab to search them all.
                      </p>
                    )}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {g.list.map((r) => <CommentCard key={`${g.key}-${r.id}`} r={r} />)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * A response's comment. Long ones collapse so a single essay cannot bury the responses under it,
 * but nothing is truncated away — the full text is one click off, in place.
 */
function Remark({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 320;
  return (
    <div className="mt-1">
      <p className={cn('whitespace-pre-wrap text-sm text-muted', long && !open && 'line-clamp-3')}>“{text}”</p>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-0.5 type-label text-brand-700 hover:underline dark:text-brand-400"
        >
          {open ? 'Show less' : `Show more (${text.length} characters)`}
        </button>
      )}
    </div>
  );
}

/**
 * The attendee's rating form, and what it becomes once they have answered.
 *
 * Five big targets, optional chips, optional comment. Only the stars are required: a farmer on a
 * phone at the edge of coverage will give you a number, and demanding a paragraph is how you end up
 * with no responses at all rather than honest ones.
 */
function RateMeetCard({ mine, windowOpen, closesAt, saving, onSubmit }: {
  mine: any; windowOpen: boolean; closesAt?: string | null; saving: boolean;
  onSubmit: (b: { rating: number; tags?: string[]; remark?: string }) => void;
}) {
  const [rating, setRating] = useState<number>(mine?.rating ?? 0);
  const [hover, setHover] = useState(0);
  const [tags, setTags] = useState<string[]>(mine?.tags ?? []);
  const [remark, setRemark] = useState<string>(mine?.remark ?? '');
  const [editing, setEditing] = useState(!mine);

  useEffect(() => {
    setRating(mine?.rating ?? 0);
    setTags(mine?.tags ?? []);
    setRemark(mine?.remark ?? '');
    setEditing(!mine);
  }, [mine]);

  // Answered, and not currently revising: show what they said rather than re-asking.
  if (mine && !editing) {
    return (
      <Space align="start" wrap size={16}>
        <div>
          <Typography.Title level={5} style={{ marginTop: 0 }}>Thanks — you rated this meet</Typography.Title>
          <div className="mt-1 text-2xl leading-none text-harvest-500">{STAR(mine.rating)}</div>
          {!!mine.tags?.length && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {mine.tags.map((t: string) => <Tag key={t}>{TAG_LABEL[t] ?? t}</Tag>)}
            </div>
          )}
          {mine.remark && <p className="mt-2 max-w-prose text-sm text-muted">“{mine.remark}”</p>}
          <div className="mt-2 text-xs text-subtle">
            {windowOpen
              ? <>You can change this until {closesAt ? formatIst(closesAt) : 'the window closes'}.</>
              : <>Feedback is now closed.</>}
          </div>
        </div>
        {windowOpen && <Button variant="default" onClick={() => setEditing(true)}>Change my rating</Button>}
      </Space>
    );
  }

  if (!windowOpen) {
    return (
      <Alert
        type="info"
        title="Feedback is closed"
        description="The window for rating this meet has passed."
      />
    );
  }

  return (
    <div>
      <Typography.Title level={5} style={{ marginTop: 0 }}>How was the meet?</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 10 }}>
        Your rating goes to the organiser{closesAt ? <> · open until {formatIst(closesAt)}</> : null}.
      </Typography.Paragraph>
      <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            onMouseEnter={() => setHover(n)}
            onClick={() => setRating(n)}
            className={cn(
              'px-0.5 text-3xl leading-none transition-colors',
              (hover || rating) >= n ? 'text-harvest-500' : 'text-subtle/40 hover:text-harvest-400',
            )}
          >
            ★
          </button>
        ))}
        {rating > 0 && <span className="ml-2 text-sm text-muted">{rating} of 5</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {Object.keys(TAG_LABEL).map((t) => {
          const on = tags.includes(t);
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTags(on ? tags.filter((x) => x !== t) : [...tags, t])}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition-colors',
                on ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/25 dark:text-brand-300'
                   : 'border-line text-muted hover:border-brand-300',
              )}
            >
              {TAG_LABEL[t]}
            </button>
          );
        })}
      </div>
      <Input.TextArea
        rows={2}
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        placeholder="Anything you want the organiser to know (optional)"
        style={{ marginTop: 12 }}
      />
      <Space style={{ marginTop: 12 }} wrap>
        <Button
          variant="primary"
          disabled={!rating}
          loading={saving}
          onClick={() => onSubmit({ rating, tags, remark: remark.trim() || undefined })}
        >
          {mine ? 'Update my rating' : 'Send my rating'}
        </Button>
        {mine && <Button variant="default" onClick={() => setEditing(false)}>Cancel</Button>}
      </Space>
    </div>
  );
}

/**
 * What the attendees said — the WORKING view, where the Report tab is the finished artefact.
 *
 * The two used to show the same thing at different sizes, and this one spent its width badly: five
 * full-bleed bars whose counts sat a screen away from their labels, and response rows with the
 * timestamp pushed to the far right across an empty gap. So it is rebuilt around what someone
 * actually does here — read the verdict, see who has not answered, chase them, and act on the
 * critical ones.
 *
 * The distribution is ONE stacked bar rather than five rows: at this size the shape of the verdict is
 * the information, and five sparse rows spent a third of the screen to say what a single bar says in
 * a strip. The responses are a two-column grid, halving the scroll.
 */
function FeedbackPanel({ summary, responses, event, canRemind, canGenerate, reminding, generating, onRemind, onGenerate, onShowPending }: {
  summary: any; responses: any[]; event: any; canRemind: boolean; canGenerate: boolean;
  reminding: boolean; generating: boolean; onRemind: () => void; onGenerate: () => void; onShowPending: () => void;
}) {
  const [band, setBand] = useState<'all' | 'positive' | 'neutral' | 'critical'>('all');
  /* Worst-first ordering means the first page is the part worth reading, so a cap costs nothing
     while several hundred cards in the DOM costs seconds. */
  const PAGE = 24;
  const [visible, setVisible] = useState(PAGE);
  const [query, setQuery] = useState('');
  useEffect(() => { setVisible(PAGE); }, [band, query]);
  if (!summary) return <div className="py-8 text-center text-muted"><Spinner /></div>;
  if (!summary.attended) return <EmptyState title="Nobody was recorded as attending, so there is nobody to ask." />;

  const total = summary.responses ?? 0;
  const pending = Math.max(0, (summary.attended ?? 0) - total);
  const dist = (n: number) => summary.distribution?.[String(n)] ?? 0;
  const counts = {
    positive: dist(5) + dist(4),
    neutral: dist(3),
    critical: dist(2) + dist(1),
  };
  const q = query.trim().toLowerCase();
  const shown = responses.filter((r) => (
    (band === 'positive' ? r.rating >= 4 : band === 'neutral' ? r.rating === 3 : band === 'critical' ? r.rating <= 2 : true)
    // At volume the useful question is "who mentioned the microphone", not "show me all 500".
    && (!q || String(r.remark ?? '').toLowerCase().includes(q) || String(r.name ?? '').toLowerCase().includes(q))
  ));
  const avgTone = summary.average == null ? 'text-subtle'
    : summary.average >= 4 ? 'text-success-600' : summary.average >= 3 ? 'text-warning-600' : 'text-danger-600';

  const Chip = ({ k, label, n, tone }: { k: typeof band; label: string; n: number; tone: string }) => (
    <button
      type="button"
      onClick={() => setBand((c) => (c === k ? 'all' : k))}
      aria-pressed={band === k}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs transition-colors',
        band === k ? `${tone} font-semibold` : 'border-line text-muted hover:border-brand-300',
      )}
    >
      {label} · {n}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* One band: the score, its shape, who answered, and the one action worth taking. */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4 rounded-card border border-line bg-surface-2/50 px-4 py-3">
        <div className="shrink-0">
          <div className="type-overline text-subtle">Average</div>
          <div className="flex items-baseline gap-1.5">
            <span className={cn('type-metric', avgTone)}>{summary.average ?? '—'}</span>
            <span className="text-sm text-subtle">of 5</span>
          </div>
          {summary.average != null && <div className="text-base leading-none text-harvest-500">{STAR(Math.round(summary.average))}</div>}
        </div>

        <div className="min-w-[220px] flex-1">
          <div className="type-overline text-subtle">How it split</div>
          {/* One stacked bar. Five separate rows said the same thing across a third of the screen. */}
          <div className="mt-1.5 flex h-3 overflow-hidden rounded-full bg-surface-2">
            {[
              ['bg-success-500', counts.positive],
              ['bg-warning-500', counts.neutral],
              ['bg-danger-500', counts.critical],
            ].map(([cls, n]) => (Number(n) > 0 ? (
              <span key={String(cls)} className={String(cls)} style={{ width: `${(Number(n) / total) * 100}%` }} />
            ) : null))}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Chip k="positive" label="Positive" n={counts.positive} tone="border-success-500 bg-success-500/10 text-success-700 dark:text-success-400" />
            <Chip k="neutral" label="Neutral" n={counts.neutral} tone="border-warning-500 bg-warning-500/10 text-warning-700 dark:text-warning-400" />
            <Chip k="critical" label="Critical" n={counts.critical} tone="border-danger-500 bg-danger-500/10 text-danger-700 dark:text-danger-400" />
          </div>
        </div>

        <div className="min-w-[190px] shrink-0">
          <div className="type-overline text-subtle">Who answered</div>
          <div className="mt-0.5 text-sm text-content">
            <strong className="tabular-nums">{total}</strong> of {summary.attended} attendees
            <span className="text-subtle"> · {summary.responseRate ?? 0}%</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-2">
            <div className="h-full rounded-full bg-brand-500" style={{ width: `${summary.responseRate ?? 0}%` }} />
          </div>
          <div className="mt-1 text-xs text-subtle">
            {pending ? (
              <button type="button" onClick={onShowPending} className="hover:underline">{pending} yet to answer</button>
            ) : 'Everyone replied'}
            {summary.onBehalfCount ? ` · ${summary.onBehalfCount} via staff` : ''}
          </div>
        </div>

        <div className="ml-auto flex shrink-0 flex-col items-end gap-1.5">
          <Space wrap>
            {canRemind && summary.windowOpen && (
              <Tooltip label={summary.remindedAt
                ? `Already reminded on ${formatIst(summary.remindedAt)}`
                : pending ? `Nudge the ${pending} who have not answered` : 'Everyone has answered'}>
                <span>
                  <Button variant="default" size="sm" icon={<Send size={15} />} disabled={!pending || !!summary.remindedAt} loading={reminding} onClick={onRemind}>
                    Remind ({pending})
                  </Button>
                </span>
              </Tooltip>
            )}
            {canGenerate && (
              <Button variant="primary" size="sm" icon={<FileText size={15} />} loading={generating} onClick={onGenerate}>
                {event?.hasReport ? 'Regenerate report' : 'Generate report'}
              </Button>
            )}
          </Space>
          <span className="text-xs text-subtle">
            {summary.windowOpen
              ? <>Open until {summary.closesAt ? formatIst(summary.closesAt) : 'soon'}</>
              : <>Closed — the score is final</>}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search comments or names…" aria-label="Search comments or names"
          prefix={<Search size={15} />}
          allowClear
          style={{ maxWidth: 320 }}
        />
        {!!q && <span className="text-sm text-muted">{shown.length} match{shown.length === 1 ? '' : 'es'}</span>}
      </div>

      {!!Object.keys(summary.tagCounts ?? {}).length && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="type-overline text-subtle">Flagged</span>
          {Object.entries(summary.tagCounts as Record<string, number>)
            .sort((a, b) => b[1] - a[1])
            .map(([t, n]) => <Tag key={t}>{TAG_LABEL[t] ?? t} · {n}</Tag>)}
        </div>
      )}

      {/* Two columns: the same responses in half the scroll, and each comment sits with its author
          instead of across a gap from a right-aligned timestamp. */}
      {shown.length === 0 ? (
        <EmptyState title={band === 'all' ? 'No ratings yet.' : 'No responses in this band.'} />
      ) : (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          {shown.slice(0, visible).map((r) => <CommentCard key={r.id} r={r} />)}
        </div>
      )}
      {shown.length > visible && (
        <div className="text-center">
          <Button variant="default" onClick={() => setVisible((v) => v + PAGE * 2)}>
            Show more ({shown.length - visible} left)
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The post-event report.
 *
 * Rendered from the SAME structured facts the server computes it from, not from the stored Markdown.
 * A report is the one artefact somebody outside the meet actually reads, and a wall of monochrome
 * text is a report nobody opens — the score, the shape of the score and the comments that explain it
 * each need to be legible at a glance, which plain prose cannot do.
 *
 * The stored Markdown remains the text record. Its narrative half — everything the model wrote,
 * above the `---` divider — is shown as prose at the top; the figures below it are drawn natively so
 * they stay in step with the data even between regenerations.
 */
function ReportPanel({ event, summary, responses, canGenerate, generating, onGenerate }: {
  event: any; summary: any; responses: any[]; canGenerate: boolean; generating: boolean; onGenerate: () => void;
}) {
  const responded = summary?.responses ?? 0;
  if (!event?.reportMd) {
    return (
      <div className="mx-auto max-w-xl py-10 text-center">
        <FileText className="mx-auto size-8 text-subtle" />
        <h4 className="mt-3 type-section-title text-content">No report yet</h4>
        <p className="mt-1 text-sm text-muted">
          The report pulls together the agenda, turnout, every rating and comment, the outcome note and
          the decisions recorded — and writes a summary over them.
        </p>
        {summary?.windowOpen && (
          <p className="mt-2 text-sm text-subtle">
            Feedback is still open until {summary.closesAt ? formatIst(summary.closesAt) : 'soon'}, so the
            figures can still move. You can generate now and regenerate once it closes.
          </p>
        )}
        {canGenerate ? (
          <Button variant="primary" icon={<FileText size={16} />} loading={generating} onClick={onGenerate} style={{ marginTop: 16 }}>
            Generate report{responded ? ` from ${responded} response${responded === 1 ? '' : 's'}` : ''}
          </Button>
        ) : (
          <p className="mt-4 text-sm text-subtle">Ask an administrator or your sales head to generate it.</p>
        )}
      </div>
    );
  }

  // Everything the model wrote sits above the divider the server inserts; the figures below it are
  // drawn from live data instead of being re-read out of the text.
  const narrative = event.reportSource === 'AI' ? String(event.reportMd).split('\n\n---\n\n')[0] : null;
  const avg = summary?.average ?? null;
  const attended = summary?.attended ?? 0;
  const expected = event.capacity ?? 0;
  const turnout = expected > 0 ? Math.round((attended / expected) * 100) : null;
  const perHead = event.budget > 0 && attended > 0 ? Number(event.budget) / attended : null;
  const scoreTone = avg == null ? 'text-subtle' : avg >= 4 ? 'text-success-600' : avg >= 3 ? 'text-warning-600' : 'text-danger-600';
  const withRemarks = responses.filter((r) => r.remark);
  /* Ordered worst-first, so the first stretch is the actionable half. A report that quotes 500
     comments is a document nobody opens. */
  const [shownComments, setShownComments] = useState(30);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <Tag tone={event.reportSource === 'AI' ? 'green' : 'blue'}>
          {event.reportSource === 'AI' ? 'AI summary + figures' : 'Figures only'}
        </Tag>
        {event.reportGeneratedAt && <span className="text-xs text-subtle">generated {formatIst(event.reportGeneratedAt)}</span>}
        {event.reportSource !== 'AI' && (
          <span className="text-xs text-subtle">— the AI model was unreachable, so these are the verified figures on their own.</span>
        )}
        {canGenerate && (
          <Button variant="default" size="sm" icon={<FileText size={15} />} loading={generating} onClick={onGenerate} className="ml-auto">
            Regenerate
          </Button>
        )}
      </div>

      {/* The four numbers someone judges a meet by, before any prose. Each carries the accent of what
          it measures — the score its own verdict colour, turnout green, reach brand, money gold — so
          the row can be read at a glance instead of parsed left to right. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <ECard
          title="Average rating" icon={<Star size={15} />}
          tone={avg == null ? 'neutral' : avg >= 4 ? 'success' : avg >= 3 ? 'warning' : 'danger'}
        >
          <div className="flex items-baseline gap-1.5">
            <span className={cn('type-metric', scoreTone)}>{avg ?? '—'}</span>
            {avg != null && <span className="text-sm text-subtle">of 5</span>}
          </div>
          {avg != null && <div className="mt-1 text-lg leading-none text-harvest-500">{STAR(Math.round(avg))}</div>}
        </ECard>
        <ECard title="Turnout" icon={<Flag size={15} />} tone="success">
          <div className="flex items-baseline gap-1.5">
            <span className="type-metric text-content">{attended}</span>
            {expected > 0 && <span className="text-sm text-subtle">of {expected}</span>}
          </div>
          {turnout != null && (
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2">
              <div className="h-full rounded-full bg-success-500" style={{ width: `${Math.min(100, turnout)}%` }} />
            </div>
          )}
          <div className="mt-1.5 text-xs text-subtle">{turnout != null ? `${turnout}% of expected` : 'no target set'}</div>
        </ECard>
        <ECard title="Responded" icon={<Send size={15} />} tone="brand">
          <div className="flex items-baseline gap-1.5">
            <span className="type-metric text-content">{responded}</span>
            <span className="text-sm text-subtle">of {attended}</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2">
            <div className="h-full rounded-full bg-brand-500" style={{ width: `${summary?.responseRate ?? 0}%` }} />
          </div>
          <div className="mt-1.5 text-xs text-subtle">{summary?.responseRate ?? 0}% of attendees</div>
        </ECard>
        <ECard title="Cost" icon={<Ticket size={15} />} tone="harvest">
          <div className="type-metric text-content">{inr(event.budget)}</div>
          <div className="mt-1.5 text-xs text-subtle">{perHead != null ? `${inr(perHead)} per attendee` : 'no attendance recorded'}</div>
        </ECard>
      </div>

      {narrative && (
        <section className="rounded-card border border-brand-500/30 bg-brand-500/[0.06] p-5">
          <h4 className="mb-2 flex items-center gap-1.5 type-overline text-brand-700 dark:text-brand-300">
            <Sparkles className="size-3.5" /> Summary
          </h4>
          <MiniMarkdown source={narrative} />
        </section>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ECard title="How it was rated" icon={<Star size={15} />} tone="brand">
            {[5, 4, 3, 2, 1].map((n) => {
              const c = summary?.distribution?.[String(n)] ?? 0;
              const pct = responded ? Math.round((c / responded) * 100) : 0;
              return (
                <div key={n} className="flex items-center gap-3 py-1">
                  <span className="w-10 shrink-0 text-xs tabular-nums text-subtle">{n} ★</span>
                  <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-2">
                    <span
                      className={cn('block h-full rounded-full', n >= 4 ? 'bg-success-500' : n === 3 ? 'bg-warning-500' : 'bg-danger-500')}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="w-16 shrink-0 text-right text-xs tabular-nums text-muted">{c} · {pct}%</span>
                </div>
              );
            })}
            {!!Object.keys(summary?.tagCounts ?? {}).length && (
              <div className="mt-4 border-t border-line pt-3">
                <div className="mb-2 type-overline text-subtle">What they flagged</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(summary.tagCounts as Record<string, number>)
                    .sort((a, b) => b[1] - a[1])
                    .map(([t, n]) => <Tag key={t}>{TAG_LABEL[t] ?? t} · {n}</Tag>)}
                </div>
              </div>
            )}
          </ECard>
        </div>

        <aside className="space-y-4">
          <ECard title="The meet" icon={<CalendarDays size={15} />} tone="sky">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3"><dt className="text-muted">Code</dt><dd className="font-mono text-xs">{event.code}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted">When</dt><dd className="text-right">{formatIst(event.startAt)}</dd></div>
              {event.venue && <div className="flex justify-between gap-3"><dt className="text-muted">Venue</dt><dd className="text-right">{event.venue}</dd></div>}
              {event.territoryName && <div className="flex justify-between gap-3"><dt className="text-muted">Territory</dt><dd className="text-right">{event.territoryName}</dd></div>}
              <div className="flex justify-between gap-3"><dt className="text-muted">Photos</dt><dd className="tabular-nums">{event.photoCount ?? 0}</dd></div>
            </dl>
          </ECard>
        </aside>
      </div>

      {/* Planned against happened, side by side — that comparison is the whole point of a report. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ECard title="What was planned" icon={<FileText size={15} />} tone="violet">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted">
            {event.description || <span className="text-subtle">No agenda was written.</span>}
          </p>
        </ECard>
        <ECard title="What happened" icon={<CheckCircle2 size={15} />} tone="success">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted">
            {event.outcomeSummary || <span className="text-subtle">No outcome note was written.</span>}
          </p>
          {event.decisions && (
            <div className="mt-3 rounded-card border border-success-500/25 bg-success-500/[0.07] p-3">
              <div className="mb-1 type-overline text-success-700 dark:text-success-400">Decisions</div>
              <p className="whitespace-pre-wrap text-sm text-content">{event.decisions}</p>
            </div>
          )}
        </ECard>
      </div>

      {/* Comments as cards. The rating appears ONCE, as a coloured edge and a badge — stacked as
          "4★ — …, 4★ — …, 5★ — …" it read as a repeated number rather than as the score it is. */}
      {!!withRemarks.length && (
        <section>
          <h4 className="mb-3 flex items-center gap-2">
            <span className="grid size-6 shrink-0 place-items-center rounded-full bg-harvest-400/20 text-harvest-700 dark:text-harvest-300">
              <MessageSquare size={15} />
            </span>
            <span className="type-overline text-muted">
              In their words
              {(summary?.responses ?? 0) > withRemarks.length
                ? ` (${withRemarks.length} of ${summary.responses} responses)`
                : ` (${withRemarks.length})`}
            </span>
          </h4>
          {withRemarks.length > 12 ? (
            <ThemedComments
              items={withRemarks}
              tagCounts={summary?.tagCounts ?? {}}
              criticalTotal={(summary?.distribution?.['1'] ?? 0) + (summary?.distribution?.['2'] ?? 0)}
              commentsTotal={summary?.responses ?? withRemarks.length}
            />
          ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {withRemarks.slice(0, shownComments).map((r) => (
              <div
                key={r.id}
                className={cn(
                  'rounded-card border border-l-4 border-line p-3',
                  r.rating >= 4 ? 'border-l-success-500' : r.rating === 3 ? 'border-l-warning-500' : 'border-l-danger-500',
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn('text-sm', r.rating >= 4 ? 'text-success-600' : r.rating === 3 ? 'text-warning-600' : 'text-danger-600')}>
                    {STAR(r.rating)}
                  </span>
                  <span className="truncate type-body-medium text-content">{r.name}</span>
                  {r.source === 'ON_BEHALF' && <Tag tone="gold">via staff</Tag>}
                </div>
                <p className="mt-1.5 whitespace-pre-wrap text-sm text-muted">“{r.remark}”</p>
                {!!r.tags?.length && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {r.tags.map((t: string) => <Tag key={t}>{TAG_LABEL[t] ?? t}</Tag>)}
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
          {withRemarks.length <= 12 && withRemarks.length > shownComments && (
            <div className="mt-3 text-center">
              <Button variant="default" onClick={() => setShownComments((v) => v + 60)}>
                Show more ({withRemarks.length - shownComments} left)
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

type PartyLike = { id: any; name?: string | null; code?: string | null; mobile?: string | null; place?: string | null };

/**
 * A picker row for a retailer or farmer.
 *
 * The identifying details are what make these lists usable — two farmers share a name far more often
 * than they share a phone number — but joined into one dot-separated run they read as noise, and the
 * name (the thing you are actually scanning for) stops standing out. So the row is built as data and
 * drawn by `renderPartyOption`, while `label` stays a plain string: that is what the Select searches,
 * so typing a village or the last digits of a number still finds someone. `text` is what a selected
 * chip shows — just the name, since the chips are a summary, not a record.
 */
const partyOption = (p: PartyLike & { kind?: 'RETAILER' | 'FARMER' }) => {
  const name = (p.name ?? '').trim() || `#${p.id}`;
  return {
    value: String(p.id),
    name,
    kind: p.kind ?? 'FARMER',
    code: p.code ?? null,
    mobile: p.mobile ?? null,
    place: p.place ?? null,
    text: name,
    label: [name, p.code, p.mobile, p.place].filter(Boolean).join(' '),
  };
};

/**
 * ONE PERSON TO PICK — their mark, their name, and the two facts that tell them from a namesake.
 *
 * A list of forty dealers in one grey, each with a grey code box, is read a row at a time and looks
 * the same after scrolling. Every option carries its identity mark now — initials on the hue that
 * name wears everywhere in the app — and the code chip takes that same hue, so a row is recognised
 * rather than re-read. A dealer is a BUSINESS, so its mark is a soft square; a farmer is a person and
 * keeps the circle.
 *
 * The two contact facts keep their own glyph colours (sky for a phone, green for a place), which is
 * what separates them at a glance now that they share a line.
 */
const renderPartyOption = (o: any) => {
  const hue = entityHue(o.name);
  return (
    <span className="flex min-w-0 items-center gap-2.5 py-0.5">
      <EntityAvatar name={o.name} size={28} square={o.kind === 'RETAILER'} />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-medium text-content">{o.name}</span>
          {o.code && (
            <span
              className="shrink-0 rounded border px-1 py-px font-mono type-caption"
              style={{
                background: `color-mix(in srgb, ${hue} 12%, transparent)`,
                borderColor: `color-mix(in srgb, ${hue} 38%, transparent)`,
                color: hue,
              }}
            >
              {o.code}
            </span>
          )}
        </span>
        {(o.mobile || o.place) && (
          <span className="flex min-w-0 items-center gap-3 text-xs text-muted">
            {o.mobile && (
              <span className="inline-flex shrink-0 items-center gap-1">
                <Phone size={11} className="shrink-0 text-sky-600 dark:text-sky-400" />
                <span className="tabular-nums">{o.mobile}</span>
              </span>
            )}
            {o.place && (
              <span className="inline-flex min-w-0 items-center gap-1">
                <MapPin size={11} className="shrink-0 text-brand-600 dark:text-brand-400" />
                <span className="truncate">{o.place}</span>
              </span>
            )}
          </span>
        )}
      </span>
    </span>
  );
};

function AddParticipantsModal({ open, onClose, eventId, territoryId, existing, onDone, recordingAttendance }:
  { open: boolean; onClose: () => void; eventId: string; territoryId?: string | null; existing: any[]; onDone: () => void;
    /** The meet is over: whoever is picked here is recorded as having attended, not invited. */
    recordingAttendance?: boolean }) {
  const [retailerIds, setRetailerIds] = useState<string[]>([]);
  const [farmerIds, setFarmerIds] = useState<string[]>([]);

  useEffect(() => { if (open) { setRetailerIds([]); setFarmerIds([]); } }, [open]);

  // Both lists are searched on the server (still sent the event's territory); picked ones stay listed.
  const retailerPick = useRetailerSearch({
    key: 'event-pick-retailers', params: territoryId ? { territoryId } : undefined, enabled: open, selected: retailerIds,
  });
  const farmerPick = useFarmerSearch({
    key: 'event-pick-farmers', params: territoryId ? { territoryId } : undefined, enabled: open, selected: farmerIds,
  });
  const retailerRows = retailerPick.rows;
  const farmerRows = farmerPick.rows;

  const haveR = useMemo(() => new Set(existing.filter((e) => e.partyType === 'RETAILER').map((e) => String(e.retailerId))), [existing]);
  const haveF = useMemo(() => new Set(existing.filter((e) => e.partyType === 'FARMER').map((e) => String(e.farmerId))), [existing]);
  const retailerOpts = useMemo(
    () => retailerRows.filter((r: any) => !haveR.has(String(r.id)))
      .map((r: any) => partyOption({ kind: 'RETAILER', id: r.id, name: r.companyName, code: r.code, mobile: r.mobile, place: r.city })),
    [retailerRows, haveR],
  );
  const farmerOpts = useMemo(
    () => farmerRows.filter((f: any) => !haveF.has(String(f.id)))
      .map((f: any) => partyOption({ kind: 'FARMER', id: f.id, name: f.fullName ?? f.name, mobile: f.mobile, place: f.city })),
    [farmerRows, haveF],
  );

  const add = useConfirmedMutation({ title: "Add the selected people to this event?", okText: "Yes, add" }, {
    mutationFn: () => request({ url: `/events/${eventId}/participants`, method: 'POST', data: { retailerIds, farmerIds } }),
    onSuccess: () => { message.success(recordingAttendance ? 'Recorded as attended' : 'Participants added'); onDone(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to add'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }}
      title={recordingAttendance ? 'Record an attendee' : 'Add participants'} width={620}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={add.isPending} disabled={!retailerIds.length && !farmerIds.length} onClick={() => add.mutate()}>
          {recordingAttendance ? 'Record as attended' : 'Add'}
        </Button>
      </>}>
      <Typography.Paragraph type="secondary" style={{ marginTop: -6 }}>
        Only retailers &amp; farmers of this event's territory are shown. Ones already on the list are hidden.
      </Typography.Paragraph>
      {/* Said plainly, because this writes an attendance fact against a named person, in your name. */}
      {recordingAttendance && (
        <Alert
          type="info"
          title="This meet is over"
          description="Anyone you pick here is recorded as having ATTENDED, in your name. Use it for walk-ins who were missed on the day."
          style={{ marginBottom: 12 }}
        />
      )}
      <div className="mb-1 flex items-center gap-1.5 type-label-strong text-sky-700 dark:text-sky-300">
        <Store size={13} className="shrink-0" />Retailers
      </div>
      <Select mode="multiple" allowClear showSearch optionFilterProp="label" optionLabelProp="text"
        filterOption={false} onSearch={retailerPick.onSearch}
        optionRender={renderPartyOption} style={{ width: '100%', marginBottom: 12 }}
        placeholder="Search by name, phone or place" value={retailerIds} onChange={setRetailerIds} options={retailerOpts} maxTagCount={2} />
      <div className="mb-1 mt-3 flex items-center gap-1.5 type-label-strong text-brand-700 dark:text-brand-300">
        <Sprout size={13} className="shrink-0" />Farmers
      </div>
      <Select mode="multiple" allowClear showSearch optionFilterProp="label" optionLabelProp="text"
        filterOption={false} onSearch={farmerPick.onSearch}
        optionRender={renderPartyOption} style={{ width: '100%' }}
        placeholder="Search by name, phone or place" value={farmerIds} onChange={setFarmerIds} options={farmerOpts} maxTagCount={2} />
    </Modal>
  );
}
